import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import * as XLSX from "xlsx";
import "@/App.css";
import axios from "axios";
import {
  Shield,
  ArrowClockwise,
  MagnifyingGlass,
  CheckCircle,
  WarningCircle,
  ClockCounterClockwise,
  Gear,
  Trash,
  Plus,
  CircleNotch,
  ArrowCounterClockwise,
  Circle,
  X,
  CaretRight,
  CaretDown,
  LockSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/sonner";
import { QualityDecisionSummary, QualityDecisionDialog } from "@/components/QualityDecisionSummary";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { NavTabs } from "@/components/NavTabs";
import { SapConnectionStatus } from "@/components/SapConnectionStatus";
import { ErpConnectionStatus } from "@/components/ErpConnectionStatus";
import { MyStockRequestsTab } from "@/components/MyStockRequestsTab";
import { ReturnToStoreTab } from "@/components/ReturnToStoreTab";
import { NpdIssueForProductionTab } from "@/components/NpdIssueForProductionTab";
import { useAuth } from "@/contexts/AuthContext";
import { formatUnit } from "../lib/formatUnit";
import { QualityNotice } from "@/components/QualityNotice";
import { RoundedUpFlag } from "../components/RoundedUpFlag";
import { formatInputQty } from "../lib/formatInputQty";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const rowKey = (r) => `${r.production_lot_id}::${r.confirmation_group_uuid}::${r.reporting_point_uuid}`;

const formatQty = (v) => (v == null ? "—" : v.toLocaleString("en-IN", { maximumFractionDigits: 2 }));

// SAP's own BOM data returns "MASS" as the raw unit label (it's actually
// a dimension/QuantityTypeCode, not a real unit - the Goods Movement bug
// fix this session confirmed the real SAP unit code is "KGM"/kilogram).
// Showing the raw "MASS" text to planners reads as a typo/bug - display
// the real, human-friendly unit instead. Internal values/keys (unit_code
// sent back to the API, etc.) are untouched - this is a display-only map.
// Sep 15 2026, user's explicit ask: show the lot's SAP end date next to Site.
const formatEndDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

// Aug 2026, matches production_confirmation_service.is_usable_stock_status()
// - flags Quality Inspection/Blocked stock in this planner-facing view too
// (same underlying store_requests data as the Store Approval screen).
const RESTRICTED_STOCK_STATUSES = new Set(["inspection", "quality inspection", "blocked", "restricted-use", "restricted", "in transit"]);
const isRestrictedStatus = (status) => RESTRICTED_STOCK_STATUSES.has((status || "").trim().toLowerCase());
// Aug 2026 bug fix: SAP's CRESTRICTED_IND flag is a SEPARATE field from
// stock_status - a row can read stock_status "Not Assigned" (normally
// usable) while still being Restricted Use. See the matching backend
// docstring on is_usable_stock_status for the real incident this was
// traced to.
const isLocationRestricted = (loc) => isRestrictedStatus(loc?.stock_status) || !!loc?.restricted;

// User's explicit ask: today posting/WIP-clearing/by-product outcomes
// only ever show as a toast at confirm-time, then vanish - this renders
// the LAST confirmation's outcome persistently on the row itself.
// Aug 27 2026 (user's explicit ask): a failed "WIP Cleared" chip now
// carries its own inline "Retry" so a stalled step can be fixed right
// here instead of only being visible/actionable elsewhere. Same for a
// failed "FG Moved" chip (SFG -> FG Goods Movement retry).
const LastConfirmationBadges = ({ data, productionLotId, lastConfKey, taskFinished, openQuantity, siteId, mainOutputProduct, unitCode, actorName, onRetried }) => {
  const [retryingWip, setRetryingWip] = useState(false);
  const [retryingFg, setRetryingFg] = useState(false);
  if (!data) return <span className="text-[#98A2B3] text-xs">—</span>;
  const chip = (ok, label) => (
    <span className={`flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm border w-fit ${
      ok ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]"
    }`}>
      {ok ? <CheckCircle size={10} weight="fill" /> : <WarningCircle size={10} weight="fill" />}
      {label}
    </span>
  );
  // Aug 2026 fix - see server.py's _all_lot_operations_finished: WIP
  // Clearing (and FG Movement) is skipped, not failed, while other
  // operations on a multi-step lot are still open - a neutral chip so
  // this never looks like a real error with a pointless Retry button.
  const neutralChip = (label, title) => (
    <span title={title} className="flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm border w-fit bg-[#F9FAFB] text-[#667085] border-[#EAECF0]">
      <Circle size={10} weight="fill" />
      {label}
    </span>
  );
  const retryWip = async () => {
    setRetryingWip(true);
    try {
      const { data: res } = await axios.post(`${API}/production-confirmation/retry-wip-clearing`, {
        production_lot_id: productionLotId, site_id: siteId, actor: actorName.trim(),
      });
      if (res.wip_clearing?.skipped) toast.info(res.wip_clearing.log || "Other operations on this lot are still open - WIP Clearing deferred");
      else if (res.wip_clearing?.success) toast.success(`WIP Clearing Run succeeded for Lot ${productionLotId}`);
      else toast.error(`WIP Clearing Run failed again: ${res.wip_clearing?.log || "see SAP for details"}`);
      onRetried?.(lastConfKey || productionLotId, { wip_clearing: res.wip_clearing });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry WIP Clearing");
    } finally {
      setRetryingWip(false);
    }
  };
  const retryFg = async () => {
    setRetryingFg(true);
    try {
      const { data: res } = await axios.post(`${API}/production-confirmation/retry-fg-movement`, {
        production_lot_id: productionLotId, site_id: siteId, main_output_product: mainOutputProduct,
        confirmed_quantity: data.confirmed_quantity, unit_code: unitCode, actor: actorName.trim(),
      });
      if (res.fg_movement?.skipped) toast.info(res.fg_movement.error_detail || "Other operations on this lot are still open - FG Goods Movement deferred");
      else if (res.fg_movement?.ok) toast.success(`Moved to ${siteId}-FG for Lot ${productionLotId}`);
      else toast.error(`FG Movement failed again: ${res.fg_movement?.error || res.fg_movement?.error_detail || "see SAP for details"}`);
      onRetried?.(lastConfKey || productionLotId, { fg_movement: res.fg_movement });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry FG Movement");
    } finally {
      setRetryingFg(false);
    }
  };
  return (
    <div className="space-y-0.5" data-testid="last-confirmation-badges">
      {chip(!!data.success, "Posted")}
      {data.success && data.confirmation_finished != null && (
        <span className={`flex items-center gap-1 text-[10px] px-1 py-0.5 rounded-sm border w-fit ${
          taskFinished ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]" : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
        }`} data-testid="last-confirmation-partial-full-badge" title={!taskFinished && openQuantity === 0 ? "All planned quantity is confirmed - the Task itself just wasn't marked Finished, so the next operation stays locked until it is (Confirm again with \"Mark as Finished\" checked, or use Finish Task)." : undefined}>
          {/* Sep 27 2026 fix (user's explicit confusion - "i finished
              same qty why its showing partial"): "Partial" used to fire
              purely off `taskFinished`, wrongly implying leftover
              QUANTITY even when Open is already 0 - the real reason was
              the Task was never marked Finished (checkbox left
              unchecked). Now distinguishes the two: only genuinely open
              quantity says "Partial"; a fully-confirmed-but-not-closed
              Task says "Not Finished" instead. */}
          {taskFinished ? <CheckCircle size={10} weight="fill" /> : <Circle size={10} weight="fill" />}
          {taskFinished ? "Full" : openQuantity > 0 ? "Partial" : "Not Finished"}
        </span>
      )}
      {data.wip_clearing != null && (
        <div className="flex items-center gap-1">
          {data.wip_clearing.skipped
            ? neutralChip("WIP Pending", data.wip_clearing.log)
            : chip(!!data.wip_clearing.success, "WIP Cleared")}
          {!data.wip_clearing.skipped && !data.wip_clearing.success && siteId && (
            <button
              type="button"
              disabled={retryingWip}
              onClick={retryWip}
              className="text-[10px] underline text-[#175CD3] hover:text-[#0E4B99] disabled:opacity-50"
              data-testid="retry-wip-clearing-button"
            >
              {retryingWip ? "Retrying..." : "Retry"}
            </button>
          )}
        </div>
      )}
      {data.byproduct_confirmation != null && chip(!!data.byproduct_confirmation.success, "By-product")}
      {data.fg_movement != null && (
        <div className="flex items-center gap-1">
          {data.fg_movement.skipped
            ? neutralChip("FG Pending", data.fg_movement.error_detail)
            : chip(!!data.fg_movement.ok, "FG Moved")}
          {!data.fg_movement.skipped && !data.fg_movement.ok && siteId && mainOutputProduct && (
            <button
              type="button"
              disabled={retryingFg}
              onClick={retryFg}
              className="text-[10px] underline text-[#175CD3] hover:text-[#0E4B99] disabled:opacity-50"
              data-testid="retry-fg-movement-button"
            >
              {retryingFg ? "Retrying..." : "Retry"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Aug 27 2026, user's explicit ask: clear step-by-step progress while
// confirming, instead of one opaque "Posting to SAP..." label for the
// whole duration.
const CONFIRM_PHASE_LABELS = {
  checking_category: "Checking item category (Finished Goods vs Sub-Assembly)...",
  moving_to_fg: "Moving stock to FG warehouse...",
};



const STATUS_TONE = {
  Released: "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]",
  Started: "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]",
  Finished: "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]",
};

// -------------------- Confirm dialog --------------------
const ConfirmDialog = ({ row, siblingRows, actorName, onClose, onConfirmed, reasons, onReasonsRefresh }) => {
  // Nov 4 2026 (Lot 82191): pre-fill what can really still be booked here -
  // the backend's ceiling - not SAP's good-output-only Open quantity.
  const [confirmedQty, setConfirmedQty] = useState(row ? (row.max_confirmable ?? row.open_quantity ?? "") : "");
  const [confirmedScrap, setConfirmedScrap] = useState("0");
  const [hasScrap, setHasScrap] = useState(false);
  const [scrapCalc, setScrapCalc] = useState(null);
  const [reason, setReason] = useState("");
  const [finished, setFinished] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingElapsed, setSavingElapsed] = useState(0);
  const [savingPhase, setSavingPhase] = useState(null);
  const [availability, setAvailability] = useState(null);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  // Oct 27 2026, user's spec: scrap on a model with Quality Activity becomes a
  // Quality Decision Note instead of a SAP posting, and a blocked lot cannot
  // be confirmed at all. Rows for every other model carry no `quality` key, so
  // nothing here shows or changes for them.
  const [sentToQuality, setSentToQuality] = useState(null);
  const [alreadyPosted, setAlreadyPosted] = useState(false);
  // testing_agent iteration_105: abort the poll loop below if this dialog
  // is closed/switched to a different row mid-poll - the background SAP
  // job itself keeps running regardless (nothing is lost), this just
  // stops updating THIS closed instance's state on a stale poll.
  const pollAbortRef = useRef(false);

  useEffect(() => {
    if (!saving) {
      setSavingElapsed(0);
      return;
    }
    const interval = setInterval(() => setSavingElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [saving]);

  useEffect(() => {
    if (row) {
      // Nov 4 2026: quality may clear part of a lot and keep the rest - the
      // cleared pieces are what production posts, locked to the piece.
      const qaCleared = row.quality?.cleared_quantity > 0 ? row.quality.cleared : null;
      setConfirmedQty(qaCleared ? String(qaCleared.confirmed_quantity ?? "") : (row.max_confirmable ?? row.open_quantity ?? ""));
      setConfirmedScrap(qaCleared ? String(qaCleared.scrap_quantity ?? 0) : "0");
      setHasScrap(qaCleared ? (qaCleared.scrap_quantity ?? 0) > 0 : false);
      setScrapCalc(null);
      // Nov 3 2026: only pre-fill the reason quality asked for if SAP still
      // has that code - Lot 82087 arrived carrying "003", which this tenant's
      // SAP does not offer, and it sat in the field looking ready to post.
      const requested = qaCleared ? (row.quality?.requested?.deviation_reason_code || "") : "";
      setReason(reasons.some((r) => r.code === requested) ? requested : "");
      // Aug 2026 (user's explicit rule): "finished" is now ALWAYS true and
      // locked - every confirmation closes the lot for good and always
      // triggers a WIP Clearing Run. No more multi-stage partial
      // confirmations against the same lot.
      setFinished(true);
      setAvailability(null);
      setConfirmError(null);
      setAlreadyPosted(false);
      pollAbortRef.current = false;
      if (row.main_output_product) {
        axios.post(`${API}/production-confirmation/scrap-calc/${encodeURIComponent(row.main_output_product)}`, {
          material_inputs: row.material_inputs || null,
          is_first_reporting_point: row.is_first_reporting_point ?? null,
        })
          .then(({ data }) => setScrapCalc(data))
          .catch(() => setScrapCalc(null));
      }
    }
    return () => {
      pollAbortRef.current = true;
    };
  }, [row]);

  // NOTE: Confirmed Scrap is a manually-entered REJECTED QUANTITY (defective
  // units the operator is reporting), unrelated to the physical by-product
  // material weight below - it is written to SAP's own ConfirmedScrap field.
  // It must NEVER auto-fill from the weight-based scrap calc.

  useEffect(() => {
    if (!row || confirmedQty === "" || Number.isNaN(Number(confirmedQty))) {
      setAvailability(null);
      return;
    }
    const timer = setTimeout(() => {
      setCheckingAvailability(true);
      axios.post(`${API}/production-confirmation/component-availability`, {
        main_output_product: row.main_output_product, confirmed_quantity: Number(confirmedQty), site_id: row.site_id,
        material_inputs: row.material_inputs || null,
      }).then(({ data }) => setAvailability(data)).catch(() => setAvailability(null)).finally(() => setCheckingAvailability(false));
    }, 400);
    return () => clearTimeout(timer);
  }, [row, confirmedQty]);

  if (!row) return null;

  // Sep 2 2026 fix (live incident, Lot 71425 - "output item posted but
  // Task status still open in SAP and Emergent"): SAP's own
  // ConfirmationFinishedIndicator on the ReportingPoint is PERMANENT
  // once set - re-submitting Confirmed Output Quantity here for a lot
  // already in this state always fails with "Changes in task or lot
  // not permitted; confirmation complete indicator set". This detects
  // that exact stuck state (output already fully posted, only the
  // separate Task-close step is missing) and swaps the whole dialog
  // for a single, safe "Finish Task" action instead.
  const stuckTaskOnly = row.confirmation_finished === true && row.task_finished === false;

  const finishTaskOnly = async () => {
    setSaving(true);
    setConfirmError(null);
    try {
      await axios.post(`${API}/production-confirmation/finish-task`, {
        production_lot_id: row.production_lot_id,
        production_lot_uuid: row.production_lot_uuid,
        confirmation_group_uuid: row.confirmation_group_uuid,
        production_task_id: row.production_task_id,
        production_task_uuid: row.production_task_uuid,
        actor: actorName.trim(),
        reporting_point_id: row.reporting_point_id,
        site_id: row.site_id,
        main_output_product: row.main_output_product,
        confirmed_quantity: row.total_confirmed_quantity,
        unit_code: row.unit_code,
        production_model_id: row.production_model_id || null,
        reporting_point_description: row.reporting_point_description || row.operation_description || null,
      });
      toast.success(`Task closed in SAP for Lot ${row.production_lot_id}`);
      onConfirmed(row);
    } catch (e) {
      setConfirmError(e.response?.data?.detail || "Failed to finish Task in SAP");
    } finally {
      setSaving(false);
    }
  };

  // Match on the LOT's real Output Products grid, not our own AI-guessed
  // scrap-family code - confirmed live that SAP's actual by-product line
  // (e.g. IRON-SCR) doesn't always match our classifier's expected code
  // (e.g. CR-SCRAP guessed from the RM description). Any output line that
  // isn't the main product IS the by-product to confirm.
  const byproductMatch = row.material_outputs?.find((mo) => mo.product_id !== row.main_output_product) || null;

  // If no output line exists AT ALL for the expected by-product, we can
  // still CREATE one from scratch (SAP supports ActionCode="01" on a
  // brand-new MaterialOutput) - reuse the main output's own target
  // logistics area as the destination, since a by-product almost always
  // shares the main output's site/storage area.
  // Sep 11 2026 bug fix, real incident (lot 72647, MAZ42117272-TA): an
  // intermediate Reporting Point (RP_10 "LEFT BLANK+PUNCHING") consumes
  // the raw material (so by-product IS genuinely expected here), but in
  // SAP only the LAST Reporting Point of a multi-op routing ("END") ever
  // carries an Output Products/MaterialOutput line + target storage area
  // - every intermediate RP's OWN material_outputs is legitimately empty
  // by SAP's own design, not a data gap. Falling back to ANY sibling RP
  // of this SAME lot that does have one (in practice always "END") gives
  // a real, valid target storage area instead of hard-blocking every
  // intermediate confirmation with "contact support".
  const mainOutputRow = row.material_outputs?.find((mo) => mo.product_id === row.main_output_product)
    || (siblingRows || []).flatMap((r) => r.material_outputs || []).find((mo) => mo.product_id === row.main_output_product)
    || null;
  const canAutoCreateByproduct = !byproductMatch && !!mainOutputRow?.target_logistics_area_id;

  // Weight-based by-product quantity - fully independent of the manual
  // Confirmed Scrap (rejection) field above.
  const qtyNum = Number(confirmedQty);
  const round6 = (n) => Math.round(n * 1e6) / 1e6;
  const byproductQty = scrapCalc?.available && confirmedQty !== "" && !Number.isNaN(qtyNum)
    ? Math.round(scrapCalc.scrap_per_unit_kg * qtyNum * 1e6) / 1e6
    : null;

  // Aug 2026 (user's explicit rule): "finished" is now a mandatory, locked
  // true on every confirmation regardless of quantity entered - no smart
  // default toggling anymore.
  const updateConfirmedQty = (value) => {
    setConfirmedQty(value);
  };

  // Nov 3 2026, user's report (Lot 82087 / RP-10: "PLAN QTY 3, 1 already
  // confirm, then why system allow 4 qty submit"): every unit booked uses up
  // a planned unit, scrapped ones included - the material for them is gone -
  // so the headroom is planned minus (confirmed + scrap) and GOOD + SCRAP of
  // this posting has to fit inside it. Until now only the good quantity was
  // compared against the open quantity, and only as a soft note. The backend
  // enforces the same rule (server._exceeds_plan) for stale tabs.
  const alreadyBooked = (row.total_confirmed_quantity || 0) + (row.total_confirmed_scrap || 0);
  const remainingQty = row.planned_quantity ? Math.max(0, round6(row.planned_quantity - alreadyBooked)) : null;
  const requestedQty = round6((Number.isNaN(qtyNum) ? 0 : qtyNum || 0) + (hasScrap ? Number(confirmedScrap) || 0 : 0));
  const exceedsPlan = remainingQty !== null && requestedQty > remainingQty + 1e-6;
  // Nov 4 2026, user's report (Lot 82191) - the ceiling is worked out by the
  // BACKEND now (production_confirmation_service.annotate_step_progress) and
  // the screen only reads it: planned - booked, capped by what the step
  // before actually passed as good, minus anything sitting with quality.
  const prevStepGood = row.previous_step_good;
  const prevStepName = row.previous_step_label;
  const heldByQuality = row.quality_held_quantity || 0;
  // Nov 4 2026, user's choice ("locked to your figures"): once quality has
  // split the lot, production posts exactly the cleared pieces.
  const qualityCleared = row.quality?.cleared_quantity > 0 ? row.quality.cleared : null;
  const bookableQty = row.max_confirmable != null ? row.max_confirmable : remainingQty;
  const exceedsPrevStep = prevStepGood != null && requestedQty > 0
    && round6(alreadyBooked + requestedQty) > prevStepGood + 1e-6;
  const exceedsHeld = heldByQuality > 0 && bookableQty !== null && requestedQty > bookableQty + 1e-6;

  const submit = async () => {
    const qty = confirmedQty === "" ? null : Number(confirmedQty);
    const scrap = confirmedScrap === "" ? null : Number(confirmedScrap);
    if (qty !== null && (Number.isNaN(qty) || qty < 0)) {
      toast.error("Confirmed Output Quantity must be a valid, non-negative number");
      return;
    }
    if (scrap !== null && (Number.isNaN(scrap) || scrap < 0)) {
      toast.error("Confirmed Scrap must be a valid, non-negative number");
      return;
    }
    if (exceedsPlan) {
      toast.error(`Only ${remainingQty} ${formatUnit(row.unit_code)} can still be booked on this step`, {
        description: `Planned ${row.planned_quantity}, already ${row.total_confirmed_quantity || 0} confirmed`
          + `${row.total_confirmed_scrap ? ` and ${row.total_confirmed_scrap} scrapped` : ""}`
          + `. You entered ${requestedQty}${hasScrap ? ` (${qtyNum || 0} good + ${Number(confirmedScrap) || 0} scrap)` : ""}.`,
      });
      return;
    }
    if (exceedsPrevStep) {
      toast.error(`Only ${prevStepGood} ${formatUnit(row.unit_code)} have cleared ${prevStepName || "the previous step"}`, {
        description: `This step already has ${alreadyBooked} booked, so ${requestedQty} more would make `
          + `${round6(alreadyBooked + requestedQty)}. Confirm more at ${prevStepName || "the previous step"} first.`,
      });
      return;
    }
    if (exceedsHeld) {
      toast.error(`${heldByQuality} ${formatUnit(row.unit_code)} are with quality and cannot be booked yet`, {
        description: `Only ${bookableQty} ${formatUnit(row.unit_code)} can be confirmed here until quality releases `
          + `${row.quality?.note_id || "the open note"}.`,
      });
      return;
    }
    // Aug 25 2026, user's explicit ask: scrap is meaningful data (QC
    // rejects, damage, etc.) - it must never be entered "by accident" as
    // a stray number, and it must always carry a reason so the shop
    // floor can trace WHY units were rejected. Gated behind the
    // "Has Scrap" checkbox itself, so this only fires when that's on.
    if (hasScrap) {
      if (scrap === null || scrap <= 0) {
        toast.error("Scrap Quantity must be greater than 0 when \"This confirmation has Scrap\" is checked");
        return;
      }
      if (!reason) {
        toast.error("Select a Scrap Reason (e.g. Quality Issue) before confirming");
        return;
      }
      // Nov 3 2026 (Lot 82087): SAP already rejected this code once, or it is
      // not one SAP offers at all - posting it again only burns another round
      // trip and leaves the lot blocked.
      const picked = reasons.find((r) => r.code === reason);
      if (!picked || picked.sap_status === "invalid") {
        const accepted = reasons.filter((r) => r.sap_status !== "invalid");
        toast.error(`SAP does not have reason code ${reason}`, {
          description: accepted.length
            ? `Pick one SAP accepts: ${accepted.map((r) => `${r.code} ${r.label}`).join(", ")}.`
            : "Ask your key user to create it in SAP Business Configuration first.",
        });
        return;
      }
    }
    // Aug 27 2026 fix (user's explicit ask - a real confirmation went
    // through with no by-product ever posted): block submission
    // up-front whenever a by-product IS expected (an RM component was
    // found) but can't actually be posted right now - either the weight
    // data is missing, or there's nowhere to post it to - instead of
    // silently letting the confirmation through with by-product fields
    // all null. Skipped only when scrapCalc genuinely found no RM
    // component at all (a real assembly-only item with no by-product).
    if (scrapCalc?.rm_product_id && !scrapCalc.available) {
      toast.error(`Cannot confirm: by-product is expected but its weight can't be calculated yet (${scrapCalc.reason}). Fix this first so the by-product is never skipped.`);
      return;
    }
    // Oct 29 2026 (Lot 81217): only a by-product with a REAL quantity needs
    // somewhere to go. A cut-to-length part whose net weight equals its gross
    // weight produces 0 kg, and blocking that deadlocked the confirmation.
    if (scrapCalc?.available && byproductQty > 0 && !byproductMatch && !canAutoCreateByproduct) {
      toast.error("Cannot confirm: a by-product is expected but there's no Output Products line to post it to and no target storage area to create one - contact support before confirming this lot.");
      return;
    }
    setSaving(true);
    setSavingPhase(null);
    setConfirmError(null);
    try {
      const { data: jobData } = await axios.post(`${API}/production-confirmation/confirm`, {
        production_lot_id: row.production_lot_id,
        production_lot_uuid: row.production_lot_uuid,
        confirmation_group_uuid: row.confirmation_group_uuid,
        reporting_point_uuid: row.reporting_point_uuid,
        reporting_point_id: row.reporting_point_id,
        main_output_product: row.main_output_product,
        unit_code: row.unit_code,
        production_task_id: row.production_task_id,
        production_task_uuid: row.production_task_uuid,
        confirmed_quantity: qty,
        confirmed_scrap: scrap,
        deviation_reason_code: reason || null,
        confirmation_finished: finished,
        site_id: row.site_id,
        byproduct_material_output_uuid: byproductMatch && byproductQty > 0 ? byproductMatch.material_output_uuid : null,
        byproduct_confirmed_quantity: byproductMatch && byproductQty > 0 ? byproductQty : null,
        byproduct_unit_code: byproductMatch && byproductQty > 0 ? byproductMatch.unit_code : null,
        // Nov 4 2026 (Lot 82274): lets the backend put this quantity on a NEW
        // line if SAP has closed the existing one for good.
        byproduct_product_id: byproductMatch && byproductQty > 0 ? byproductMatch.product_id : null,
        byproduct_target_logistics_area_id: byproductMatch && byproductQty > 0
          ? (byproductMatch.target_logistics_area_id || mainOutputRow?.target_logistics_area_id || null) : null,
        new_byproduct_product_id: canAutoCreateByproduct && byproductQty > 0 ? scrapCalc.scrap_family.expected_byproduct_code : null,
        new_byproduct_target_logistics_area_id: canAutoCreateByproduct && byproductQty > 0 ? mainOutputRow.target_logistics_area_id : null,
        new_byproduct_confirmed_quantity: canAutoCreateByproduct && byproductQty > 0 ? byproductQty : null,
        new_byproduct_unit_code: canAutoCreateByproduct && byproductQty > 0 ? "KGM" : null,
        production_model_id: row.production_model_id || null,
        reporting_point_description: row.reporting_point_description || row.operation_description || null,
        material_inputs: row.material_inputs || null,
        is_first_reporting_point: row.is_first_reporting_point ?? null,
        actor: actorName.trim(),
      });
      // Runs as a background job (Aug 2026 fix) - posting to SAP can take
      // long enough (by-product + main + finish task + WIP clearing, up
      // to 4 sequential SOAP calls) to blow past the platform's ingress
      // timeout and surface as a raw, unhelpful Cloudflare error instead
      // of a real SAP message (reproduced live on Lot 70222 with short
      // stock). Polling here instead means that can never happen again.
      // testing_agent iteration_105: bounded to a 3-min deadline (well
      // above the realistic worst case for up to 4 sequential SOAP
      // calls) and retries a single transient status-GET failure instead
      // of immediately reporting a false failure - the background job
      // keeps running server-side either way, nothing is lost.
      if (jobData.quality_decision) {
        setSentToQuality(jobData.quality_decision);
        toast.success(`Scrap sent to quality as ${jobData.quality_decision.note_id}`, {
          description: `Lot ${row.production_lot_id} stays blocked until quality accepts the scrap - nothing posted to SAP.`,
        });
        return;
      }
      const deadline = Date.now() + 3 * 60 * 1000;
      let job = null;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 3000));
        if (pollAbortRef.current) return;
        try {
          const { data } = await axios.get(`${API}/production-confirmation/confirm/status/${jobData.job_id}`);
          job = data;
          // Aug 27 2026 (user's explicit ask): "show progress with steps
          // clearly" while the backend is live-checking category/moving
          // stock to FG, instead of one opaque "Posting to SAP..." label.
          setSavingPhase(job.status);
        } catch {
          continue; // transient network hiccup - just retry on the next tick
        }
        if (job.status === "done" || job.status === "failed") break;
        job = null;
      }
      if (pollAbortRef.current) return;
      if (!job) {
        setConfirmError(`Still waiting on SAP for Lot ${row.production_lot_id} after 3 minutes - it may still complete in the background. Check History shortly before retrying to avoid a duplicate confirmation.`);
        return;
      }
      if (job.status === "failed") {
        // Persistent on-screen banner instead of a toast (Aug 2026 user
        // feedback: "the error is a toast, instead show a message on
        // screen user will close so it does not disappear") - a real SAP
        // rejection/timeout message here is important enough that it
        // must stay visible until the user dismisses it themselves.
        setConfirmError(job.error || "Failed to post confirmation to SAP");
        return;
      }
      const data = job.result;
      if (data.success) {
        toast.success(`Confirmation posted to SAP for Lot ${row.production_lot_id}`);
        if (data.byproduct_confirmation) {
          if (data.byproduct_confirmation.success) {
            toast.success(`By-product ${byproductMatch?.product_id || "quantity"} (${byproductQty ?? 0} ${formatUnit(byproductMatch?.unit_code)}) posted to SAP`);
          } else {
            toast.error(`By-product quantity failed to post: ${data.byproduct_confirmation.logs?.map((l) => l.note).join("; ") || "see history for details"}`);
          }
        }
        if (finished && data.wip_clearing) {
          if (data.wip_clearing.skipped) {
            toast.info(data.wip_clearing.log || `WIP Clearing deferred for Lot ${row.production_lot_id} - other operations are still open`);
          } else if (data.wip_clearing.success) {
            toast.success(`WIP Clearing Run triggered for Lot ${row.production_lot_id}`);
          } else {
            toast.error(`WIP Clearing Run failed: ${data.wip_clearing.log || "see history for details"}`);
          }
        }
        if (data.fg_movement) {
          if (data.fg_movement.ok) {
            toast.success(`Finished Goods moved to ${row.site_id}-FG for Lot ${row.production_lot_id}`);
          } else {
            toast.error(`FG Movement failed: ${data.fg_movement.error || "see history for details"}`);
          }
        }
        onConfirmed(row);
      } else {
        // Sep 3 2026 fix: the main confirmation can fail AFTER a
        // by-product confirmation already succeeded (a real, irreversible
        // SAP write) - surface that explicitly so it's never mistaken for
        // "nothing happened" (see server.py's matching fix, Lot 71541).
        if (data.byproduct_confirmation?.success) {
          toast.warning(`By-product ${byproductMatch?.product_id || "quantity"} was posted to SAP, but the main confirmation failed below - retry once fixed (the by-product won't be re-posted).`);
        }
        // clarified_error (Aug 2026, testing_agent iteration_105): a
        // normal SAP business rejection (no exception) now gets the same
        // readable guidance as an exception/timeout path instead of raw
        // SAP log text like "Modification failed".
        setConfirmError(data.clarified_error || `SAP reported an issue: ${data.logs?.map((l) => l.note).join("; ") || "see history for details"}`);
      }
    } catch (e) {
      if (!pollAbortRef.current) {
        // Oct 17 2026 (Lot 78628): the backend re-reads SAP right before
        // posting and answers 409 when this reporting point is already
        // closed - that is not a failure, the quantity is already in SAP.
        if (e.response?.status === 409) setAlreadyPosted(true);
        setConfirmError(e.response?.data?.detail || "Failed to post confirmation to SAP");
      }
    } finally {
      setSaving(false);
      // Nov 3 2026: SAP's verdict on the reason code was just recorded
      // server-side, so pull the list again - a code SAP rejected greys out
      // immediately, in this same open dialog.
      onReasonsRefresh?.();
    }
  };

  return (
    <Dialog open={!!row} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md" data-testid="confirm-production-dialog">
        <DialogHeader>
          <DialogTitle>Confirm Production Task</DialogTitle>
          <DialogDescription>
            Lot {row.production_lot_id} · {row.main_output_product || "—"} · Reporting Point {row.reporting_point_description || row.reporting_point_id || "—"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <QualityNotice quality={row.quality} sentToQuality={sentToQuality} unit={formatUnit(row.unit_code)} />
          {alreadyPosted ? (
            <Alert className="rounded-sm border-[#FEDF89] bg-[#FFFAEB]" data-testid="already-confirmed-banner">
              <WarningCircle size={16} />
              <AlertTitle className="font-heading text-sm">Already confirmed in SAP</AlertTitle>
              <AlertDescription className="font-sans text-[13px] break-words">{confirmError}</AlertDescription>
            </Alert>
          ) : confirmError && (
            <Alert variant="destructive" className="relative pr-9 rounded-sm border-[#F04438]/40 bg-[#FEF3F2]" data-testid="confirm-error-banner">
              <WarningCircle size={16} />
              <AlertTitle className="font-heading text-sm">Confirmation failed</AlertTitle>
              <AlertDescription className="font-sans text-[13px] break-words">
                {confirmError}
                {/* Nov 3 2026 (Lot 82087): SAP rejects reason codes that were never set up in this
                    tenant, so point straight at the ones it has accepted instead of guessing. */}
                {/reason code .* not valid/i.test(confirmError) && (
                  <span className="block mt-1.5" data-testid="confirm-error-reason-hint">
                    That reason does not exist in your SAP configuration and has now been greyed out.
                    {reasons.some((r) => r.sap_status === "valid")
                      ? ` Use one SAP accepts: ${reasons.filter((r) => r.sap_status === "valid").map((r) => `${r.code} ${r.label}`).join(", ")}.`
                      : " Ask your key user to create it in SAP Business Configuration."}
                  </span>
                )}
              </AlertDescription>
              <button
                type="button"
                onClick={() => setConfirmError(null)}
                className="absolute top-3 right-3 text-[#B42318]/70 hover:text-[#B42318]"
                data-testid="confirm-error-banner-close"
                aria-label="Dismiss error"
              >
                <X size={16} />
              </button>
            </Alert>
          )}
          {alreadyPosted ? null : stuckTaskOnly ? (
            <div className="text-sm text-[#344054] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-3 py-2.5" data-testid="stuck-task-only-banner">
              Output already fully posted to SAP ({formatQty(row.planned_quantity)} {formatUnit(row.unit_code)}) - only the
              Task itself is still open. Re-entering a quantity would be rejected by SAP since this ReportingPoint is
              already closed. Click <strong>Finish Task</strong> below to close it - no quantity is sent.
              {(row.remaining_quantity ?? 0) > 0 && (
                <> If quantity is still open and you need to post it, close this box and press <strong>Reopen Step</strong> on
                the row instead.</>
              )}
            </div>
          ) : (
          <>
          <div className="text-xs text-[#667085] bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-3 py-2">
            Planned: <strong className="text-[#1D2939]">{formatQty(row.planned_quantity)}</strong> {formatUnit(row.unit_code)} ·
            {" "}Open: <strong className="text-[#1D2939]">{formatQty(row.open_quantity)}</strong> {formatUnit(row.unit_code)}
            {/* Nov 3 2026 (Lot 82087): the scrap already booked is part of the
                plan too, so show what is genuinely left to book. */}
            {row.total_confirmed_scrap > 0 && (
              <> · Already scrapped: <strong className="text-[#B54708]">{formatQty(row.total_confirmed_scrap)}</strong> {formatUnit(row.unit_code)}</>
            )}
            {/* Nov 4 2026 (Lot 82191): the only number that matters to the
                operator - worked out by the backend, scrap, the previous
                step's good output and quality's hold all taken off. */}
            {bookableQty !== null && (
              <> · Can still book: <strong className={bookableQty > 0 ? "text-[#1D2939]" : "text-[#B42318]"} data-testid="confirm-bookable-qty">{formatQty(bookableQty)}</strong> {formatUnit(row.unit_code)}</>
            )}
            {prevStepGood != null && (
              <> · Passed by {prevStepName}: <strong className="text-[#1D2939]">{formatQty(prevStepGood)}</strong> {formatUnit(row.unit_code)}</>
            )}
            {heldByQuality > 0 && (
              <> · <span className="text-[#B54708]">With quality: <strong>{formatQty(heldByQuality)}</strong> {formatUnit(row.unit_code)}</span></>
            )}
          </div>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Confirmed Output Quantity</Label>
            <Input type="number" value={confirmedQty} onChange={(e) => updateConfirmedQty(e.target.value)} disabled={!!qualityCleared} data-testid="confirm-qty-input" />
            {row.quality?.note_id && (row.quality.cleared_quantity > 0 || row.quality.pending_quantity > 0 || row.quality.status === "pending") && (
              <div className="mt-2">
                <QualityDecisionSummary row={row} unit={formatUnit(row.unit_code)} />
              </div>
            )}
            {qualityCleared && (
              <p className="text-xs text-[#026AA2] mt-1" data-testid="confirm-qty-locked-by-quality">
                Locked to quality's figures: {formatQty(qualityCleared.confirmed_quantity)} good
                {qualityCleared.scrap_quantity > 0 ? ` and ${formatQty(qualityCleared.scrap_quantity)} scrap` : ""}.
              </p>
            )}
            {exceedsPlan && (
              <p className="text-xs text-[#B42318] font-bold mt-1" data-testid="confirm-qty-exceeds-plan-warning">
                {requestedQty} {formatUnit(row.unit_code)}{hasScrap ? ` (${qtyNum || 0} good + ${Number(confirmedScrap) || 0} scrap)` : ""} is
                more than this step can still take - only {remainingQty} {formatUnit(row.unit_code)} of the planned
                {" "}{formatQty(row.planned_quantity)} is left. Reduce it, or raise the planned quantity on the lot in SAP first.
              </p>
            )}
            {!exceedsPlan && exceedsPrevStep && (
              <p className="text-xs text-[#B42318] font-bold mt-1" data-testid="confirm-qty-exceeds-prev-step-warning">
                Only {formatQty(prevStepGood)} {formatUnit(row.unit_code)} have cleared {prevStepName} so far and
                {" "}{formatQty(alreadyBooked)} is already booked here - {requestedQty} more would make
                {" "}{formatQty(round6(alreadyBooked + requestedQty))}. Confirm more at {prevStepName} first.
              </p>
            )}
            {!exceedsPlan && !exceedsPrevStep && exceedsHeld && (
              <p className="text-xs text-[#B42318] font-bold mt-1" data-testid="confirm-qty-quality-hold-warning">
                {formatQty(heldByQuality)} {formatUnit(row.unit_code)} are with quality
                {row.quality?.note_id ? ` (${row.quality.note_id})` : ""} and cannot be booked until it is released -
                {" "}only {formatQty(bookableQty)} {formatUnit(row.unit_code)} can be confirmed here.
              </p>
            )}
          </div>

          {checkingAvailability && <p className="text-xs text-[#98A2B3]">Checking component stock...</p>}
          {availability?.checked && (
            <div className="border border-[#EAECF0] rounded-sm overflow-hidden" data-testid="component-availability-panel">
              <div className="bg-[#F9FAFB] px-3 py-1.5 text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">
                Component Stock Check {row.site_id ? `(Site ${row.site_id})` : ""}
              </div>
              <div className="max-h-48 overflow-y-auto divide-y divide-[#EAECF0]">
                {availability.components.map((c) => (
                  <div key={c.product_id} className="px-3 py-1.5 text-xs" data-testid={`component-row-${c.product_id}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[#344054] truncate mr-2">{c.product_id}{c.description ? ` - ${c.description}` : ""}</span>
                      <span className={`shrink-0 tabular-nums text-right ${c.sufficient ? "text-[#027A48]" : c.available_qty === null ? "text-[#98A2B3]" : "text-[#B42318] font-bold"}`}>
                        {c.available_qty === null ? "no stock data" : `${formatQty(c.available_qty)} / ${formatInputQty(c.required_qty)} ${formatUnit(c.unit_of_measure)}`}
                        {c.sufficient ? " ✓" : c.available_qty !== null ? " ✗" : ""}
                        <RoundedUpFlag component={c} />
                      </span>
                    </div>
                    {/* Per-warehouse/stock-status breakdown at this site - user's
                        explicit ask (Aug 2026): show SITE + Warehouse (e.g. RM/
                        SFG/FG) + Stock Status, not just a single aggregate qty. */}
                    {c.locations && c.locations.length > 0 && (
                      <div className="mt-0.5 pl-2 text-[11px] text-[#667085] space-y-0.5" data-testid={`component-locations-${c.product_id}`}>
                        {c.locations.map((loc, li) => (
                          <div key={li}>
                            {row.site_id}{loc.warehouse ? ` / ${loc.warehouse}` : ""}{loc.stock_status ? ` (${loc.stock_status})` : ""}: {formatQty(loc.qty)} {formatUnit(c.unit_of_measure)}
                          </div>
                        ))}
                      </div>
                    )}
                    {c.backflush_area && (
                      <div className={`mt-0.5 pl-2 text-[11px] ${c.backflush_area_short ? "text-[#B42318] font-bold" : "text-[#027A48]"}`} data-testid={`component-backflush-${c.product_id}`}>
                        SAP backflushes from {c.backflush_area}: {c.backflush_area_qty === null ? "unknown" : `${formatQty(c.backflush_area_qty)} ${formatUnit(c.unit_of_measure)}`}
                        {c.backflush_area_short ? " - SAP will reject this confirmation" : ""}
                      </div>
                    )}
                  </div>
                ))}
                {availability.components.length === 0 && <div className="px-3 py-1.5 text-xs text-[#98A2B3]">No active components in cached BOM.</div>}
              </div>
              {availability.components.some((c) => c.backflush_area_short) && (
                <div className="bg-[#FEF3F2] px-3 py-1.5 text-[11px] text-[#B42318] font-bold" data-testid="backflush-area-warning">
                  SAP consumes these components from{" "}
                  {[...new Set(availability.components.filter((c) => c.backflush_area_short).map((c) => c.backflush_area))].join(", ")}
                  , which does not hold enough stock - SAP will reject this confirmation even though the stock exists elsewhere at this site.
                  Move the stock into that area, or correct the component source/supply area on this production model in SAP.
                </div>
              )}
              {availability.components.some((c) => !c.sufficient && c.available_qty !== null) && (
                <div className="bg-[#FFFAEB] px-3 py-1.5 text-[11px] text-[#B54708]">Some components may be short - SAP's backflush could reject this confirmation.</div>
              )}
            </div>
          )}
          {availability && !availability.checked && (
            <p className="text-[11px] text-[#98A2B3]">{availability.reason}</p>
          )}

          {scrapCalc?.available && (
            <div className="text-xs text-[#344054] bg-[#F9FAFB] border border-[#EAECF0] rounded-sm px-3 py-2" data-testid="scrap-calc-info">
              <div className="font-bold font-heading uppercase tracking-wide text-[10px] text-[#667085] mb-1">Auto-calculated from {scrapCalc.rm_product_id}{scrapCalc.rm_description ? ` (${scrapCalc.rm_description})` : ""}</div>
              Gross Weight: <strong data-testid="scrap-calc-gross">{scrapCalc.gross_weight_kg}</strong> kg ·
              {" "}Net Weight: <strong data-testid="scrap-calc-net">{scrapCalc.net_weight_kg}</strong> kg ·
              {" "}Scrap/unit: <strong data-testid="scrap-calc-per-unit">{scrapCalc.scrap_per_unit_kg}</strong> kg
              {scrapCalc.scrap_family && (
                <div className="mt-1 text-[#667085]" data-testid="scrap-calc-family">
                  Expected by-product: {scrapCalc.scrap_family.family} ({scrapCalc.scrap_family.expected_byproduct_code})
                  {byproductMatch
                    ? <span className="text-[#027A48]" data-testid="byproduct-match-found"> · will post {byproductQty ?? 0} {formatUnit(byproductMatch.unit_code)} of {byproductMatch.product_id} to SAP</span>
                    : canAutoCreateByproduct
                      ? <span className="text-[#0E7C86]" data-testid="byproduct-auto-create"> · no output line exists yet - will auto-create it and post {byproductQty ?? 0} KGM to SAP</span>
                      : <span className="text-[#B54708]" data-testid="byproduct-match-missing"> · no by-product output line found on this lot - only Confirmed Quantity will be posted</span>}
                </div>
              )}
            </div>
          )}
          {scrapCalc && !scrapCalc.available && scrapCalc.rm_product_id && (
            <div className="text-[11px] text-[#B42318] bg-[#FEF3F2] border border-[#FECDCA] rounded-sm px-3 py-2 flex items-start gap-1.5" data-testid="scrap-calc-unavailable">
              <WarningCircle size={13} weight="fill" className="shrink-0 mt-0.5" />
              <span><strong>Cannot confirm yet:</strong> {scrapCalc.reason}</span>
            </div>
          )}
          <div className="border border-[#D0D5DD] rounded-sm p-3 space-y-3" data-testid="scrap-section">
            <div className="flex items-center gap-2">
              <Checkbox
                id="has-scrap-cb"
                checked={hasScrap}
                onCheckedChange={(checked) => {
                  setHasScrap(!!checked);
                  if (!checked) { setConfirmedScrap("0"); setReason(""); }
                }}
                data-testid="confirm-has-scrap-checkbox"
              />
              <Label htmlFor="has-scrap-cb" className="text-xs font-bold text-[#344054]">This confirmation has Scrap / Rejected units (QC issue, damage, etc.)</Label>
            </div>
            {hasScrap && (
              <>
                <div>
                  <Label className="text-xs font-bold text-[#344054]">Scrap Quantity (Rejected Qty) *</Label>
                  <Input type="number" value={confirmedScrap} onChange={(e) => setConfirmedScrap(e.target.value)} disabled={!!qualityCleared} data-testid="confirm-scrap-input" />
                  <p className="text-[11px] text-[#98A2B3] mt-0.5">Manually enter rejected/defective units. Unrelated to the by-product weight above.</p>
                </div>
                <div>
                  <Label className="text-xs font-bold text-[#344054]">Scrap Reason *</Label>
                  <Select value={reason} onValueChange={setReason}>
                    <SelectTrigger data-testid="confirm-deviation-reason-select"><SelectValue placeholder="Select a reason..." /></SelectTrigger>
                    <SelectContent>
                      {reasons.map((r) => (
                        <SelectItem
                          key={r.code}
                          value={r.code}
                          disabled={r.sap_status === "invalid"}
                          data-testid={`confirm-reason-option-${r.code}`}
                        >
                          {r.code} - {r.label}
                          {r.sap_status === "invalid" && <span className="text-[#B42318]"> · not set up in SAP</span>}
                          {r.sap_status === "valid" && <span className="text-[#027A48]"> · accepted by SAP</span>}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[11px] text-[#98A2B3] mt-0.5">
                    Required whenever scrap units are recorded. Greyed-out reasons were rejected by SAP - your key user has to create
                    them in SAP Business Configuration before they can be used.
                  </p>
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="finished-cb" checked={finished} disabled data-testid="confirm-finished-checkbox" />
            <Label htmlFor="finished-cb" className="text-sm text-[#344054]">Mark this task as finished (mandatory - every confirmation closes this lot)</Label>
          </div>
          <p className="text-[11px] text-[#98A2B3]">Component/input quantities are NOT sent - SAP's backflush auto-consumes BOM inputs from the confirmed output.</p>
          </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="confirm-cancel-button">Cancel</Button>
          {alreadyPosted ? (
            <Button onClick={() => onConfirmed(row)} data-testid="already-confirmed-close-button">Close &amp; refresh list</Button>
          ) : stuckTaskOnly ? (
            <Button onClick={finishTaskOnly} disabled={saving} data-testid="finish-task-only-button">
              {saving ? "Closing Task in SAP..." : "Finish Task"}
            </Button>
          ) : (
          <Button onClick={submit} disabled={row.quality?.blocked || !!sentToQuality || saving || checkingAvailability || exceedsPlan || exceedsPrevStep || exceedsHeld || (scrapCalc?.rm_product_id && !scrapCalc?.available)} data-testid="confirm-submit-button">
            {saving ? `${CONFIRM_PHASE_LABELS[savingPhase] || "Posting to SAP"} (${savingElapsed}s)...` : checkingAvailability ? "Checking stock..." : exceedsPlan ? "Quantity over plan" : exceedsPrevStep ? `Over what ${prevStepName || "the previous step"} passed` : exceedsHeld ? "Quantity held by quality" : (scrapCalc?.rm_product_id && !scrapCalc?.available) ? "Fix weight data first" : "Post Confirmation"}
          </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// -------------------- Manage Reasons dialog --------------------
const ManageReasonsDialog = ({ open, onClose, reasons, onChanged }) => {
  const [code, setCode] = useState("");
  const [label, setLabel] = useState("");

  const add = async () => {
    if (!code.trim() || !label.trim()) return;
    const isUpdate = reasons.some((r) => r.code === code.trim());
    try {
      const { data } = await axios.post(`${API}/production-confirmation/deviation-reasons`, { code: code.trim(), label: label.trim() });
      onChanged(data.reasons);
      setCode("");
      setLabel("");
      toast.success(isUpdate ? `Updated reason ${code.trim()}` : `Added reason ${code.trim()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save deviation reason");
    }
  };

  const remove = async (c) => {
    try {
      const { data } = await axios.delete(`${API}/production-confirmation/deviation-reasons/${c}`);
      onChanged(data.reasons);
      toast.success(`Removed reason ${c}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to delete deviation reason");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="manage-reasons-dialog">
        <DialogHeader>
          <DialogTitle>Manage Deviation Reasons</DialogTitle>
          <DialogDescription>These are the reasons SAP's own Deviation Reason list offers - a code SAP does not have is rejected on posting, so only add one after your key user has created it in SAP.</DialogDescription>
        </DialogHeader>
        <div className="max-h-56 overflow-y-auto space-y-1">
          {reasons.map((r) => (
            <div key={r.code} className="flex items-center justify-between px-2 py-1.5 rounded-sm bg-[#F9FAFB] border border-[#EAECF0]" data-testid={`reason-row-${r.code}`}>
              <span className="text-sm text-[#344054]">
                <strong>{r.code}</strong> - {r.label}
                {/* Nov 3 2026: SAP's own verdict, learned from each posting
                    attempt (production_confirmation_service.record_reason_outcome). */}
                {r.sap_status === "invalid" && <span className="ml-1.5 text-[11px] text-[#B42318]">rejected by SAP</span>}
                {r.sap_status === "valid" && <span className="ml-1.5 text-[11px] text-[#027A48]">accepted by SAP</span>}
                {r.sap_status === "unknown" && <span className="ml-1.5 text-[11px] text-[#98A2B3]">not used yet</span>}
              </span>
              <button onClick={() => remove(r.code)} className="text-[#B42318] hover:text-[#912018]" data-testid={`reason-delete-${r.code}`}>
                <Trash size={14} weight="bold" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2 pt-2 border-t border-[#EAECF0]">
          <Input placeholder="Code (e.g. 009)" value={code} onChange={(e) => setCode(e.target.value)} className="w-28" data-testid="reason-code-input" />
          <Input placeholder="Label" value={label} onChange={(e) => setLabel(e.target.value)} data-testid="reason-label-input" />
          <Button onClick={add} data-testid="reason-add-button"><Plus size={14} /></Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// -------------------- Create Production Order tab --------------------
const ACTIVE_JOBS_STORAGE_KEY = "productionConfirmationActiveJobs";
const POLL_INTERVAL_MS = 4000;

// These 2 statuses PAUSE the automated pipeline for a human action (store
// issuing stock, or the requester deciding on a partial issue).
const PAUSED_STATUSES = ["waiting_store_approval", "partial_pending_planner"];

const PHASE_LABELS = {
  running: "Submitting...",
  checking_item: "Checking Item",
  checking_stock: "Checking Stock",
  creating_proposal: "Creating Proposal",
  waiting_for_order: "SAP Converting to Order",
  releasing_order: "Releasing Order",
  waiting_store_approval: "Waiting for Store",
  partial_pending_planner: "Awaiting Your Decision",
  paused_sap_block: "Paused - SAP Rejected",
};

// The automated pipeline, in order. The SERVER decides which of these are
// done/current/pending and how long each one really took (job.progress, see
// server._order_job_progress) - this list is only the running order and the
// fallback for a job doc created before that timeline existed.
const ORDER_STEP_KEYS = ["checking_item", "checking_stock", "creating_proposal", "waiting_for_order", "releasing_order"];

const formatDuration = (seconds) => {
  const s = Math.max(0, Math.round(seconds || 0));
  return s >= 60 ? `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s` : `${s}s`;
};

// Nov 5 2026, user's report ("I don't think it reflects the true state of
// what is happening inside, or the timer"): every figure shown here is the
// server's own measurement of the job - the step it is really on, how many
// seconds each step took, and SAP's time kept separate from the time spent
// waiting for a person. The browser only ticks the headline clock between
// polls, re-based to the server's number on every poll.
const OrderStepTracker = ({ status, elapsedSeconds, progress }) => {
  const steps = progress?.steps?.length
    ? progress.steps
    : ORDER_STEP_KEYS.map((key, idx) => {
        const rawIndex = ORDER_STEP_KEYS.indexOf(status);
        const currentIndex = rawIndex === -1 ? 0 : rawIndex;
        return { key, state: currentIndex > idx ? "done" : currentIndex === idx ? "current" : "pending", seconds: 0 };
      });
  const waiting = progress?.waiting_for_person_seconds || 0;
  return (
    <div className="space-y-0.5" data-testid="order-step-tracker">
      <div className="flex items-center gap-1 flex-wrap">
        {steps.map((step, idx) => (
          <span key={step.key} className="flex items-center gap-1">
            <span
              className={`flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded-sm border whitespace-nowrap ${
                step.state === "done" ? "bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]"
                : step.state === "current" ? "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]"
                : step.state === "skipped" ? "bg-[#F9FAFB] text-[#98A2B3] border-[#EAECF0] line-through"
                : "bg-[#F9FAFB] text-[#98A2B3] border-[#EAECF0]"
              }`}
              data-testid={`order-step-${step.key}`}
              data-step-state={step.state}
            >
              {step.state === "done" ? <CheckCircle size={11} weight="fill" /> : step.state === "current" ? <CircleNotch size={11} className="animate-spin" /> : <Circle size={11} />}
              {PHASE_LABELS[step.key]}
              {step.seconds > 0 && <span className="tabular-nums opacity-70">{formatDuration(step.seconds)}</span>}
            </span>
            {idx < steps.length - 1 && <span className="text-[#D0D5DD]">{"\u2192"}</span>}
          </span>
        ))}
      </div>
      <div className="text-[11px] text-[#667085] tabular-nums" data-testid="order-step-elapsed">
        {formatDuration(elapsedSeconds)} total
        {waiting > 0 && (
          <span data-testid="order-step-waiting-split">
            {" "}· {formatDuration(progress.working_seconds)} in SAP · {formatDuration(waiting)} waiting for a person
          </span>
        )}
      </div>
    </div>
  );
};

// A paused row shows a Badge instead of the step tracker, so the split the
// user asked for (SAP's time vs a person's) is repeated here - this is
// exactly where it matters, since the clock is now running on a human.
const PausedTimingNote = ({ job, index }) => {
  const p = job.progress;
  if (!p?.elapsed_seconds) return null;
  return (
    <div className="text-[11px] text-[#667085] tabular-nums mt-1" data-testid={`active-order-paused-timing-${index}`}>
      {formatDuration(p.working_seconds)} in SAP · {formatDuration(p.waiting_for_person_seconds)} waiting for a person
    </div>
  );
};

// Oct 30 2026, user's ask: SAP creates the Order In Preparation and this app can
// read it straight away, so the number goes on screen the moment it is read -
// with what is being done to it. A release that has not gone through says so,
// names the next automatic attempt and offers the manual route rather than
// leaving the floor guessing.
const OrderReleaseNote = ({ job, index, onUseOrderId }) => {
  const secondsLeft = job.nextReleaseAttemptAt
    ? Math.max(0, Math.round((new Date(job.nextReleaseAttemptAt).getTime() - Date.now()) / 1000))
    : null;
  if (job.orderReleaseState !== "retrying") {
    return (
      <div className="text-[11px] text-[#175CD3] mt-1" data-testid={`active-order-release-detail-${index}`}>
        Order <strong>{job.productionOrderId}</strong> is created in SAP - processing its release now
      </div>
    );
  }
  return (
    <div className="text-[11px] text-[#B54708] mt-1 space-y-0.5" data-testid={`active-order-release-detail-${index}`}>
      <div>
        Order <strong>{job.productionOrderId}</strong> is created in SAP but still In Preparation
        {job.orderReleaseError ? ` - SAP said: ${job.orderReleaseError}` : ""}
      </div>
      <div>
        Attempt #{job.orderReleaseAttempts || 1} done. Next automatic release{" "}
        {secondsLeft === null ? "shortly" : secondsLeft > 0 ? `in ${secondsLeft}s` : "now"} - either wait for it, or
        release order {job.productionOrderId} yourself in SAP
        {onUseOrderId && (
          <>
            {" or "}
            <button
              type="button"
              onClick={() => onUseOrderId(job.productionOrderId)}
              className="underline font-bold"
              data-testid={`active-order-release-manual-${index}`}
            >
              from the Release Production Order box
            </button>
          </>
        )}
      </div>
    </div>
  );
};

const CreateOrderTab = ({ actorName }) => {
  const [materialId, setMaterialId] = useState("");
  const [siteId, setSiteId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [unitCode, setUnitCode] = useState("EA");
  const [requestedEndDate, setRequestedEndDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Oct 22 2026, user's explicit ask: the output item's Valuation must be
  // Active on Moving Average at that site before a proposal is created. The
  // reason stays on screen - a toast is gone before anyone can read a
  // master-data instruction.
  const [valuationBlock, setValuationBlock] = useState(null);
  const [releaseOrderId, setReleaseOrderId] = useState("");
  const [releasing, setReleasing] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  // Sep 12 2026, user's explicit ask: filter header on this history
  // table - date range refetches from Mongo (fast, "created on"), the
  // rest filter client-side against the already-loaded rows.
  const [historyStartDate, setHistoryStartDate] = useState("");
  const [historyEndDate, setHistoryEndDate] = useState("");
  const [historyResultFilter, setHistoryResultFilter] = useState("all");
  const [historyModelFilter, setHistoryModelFilter] = useState("");
  const [historyProductFilter, setHistoryProductFilter] = useState("");
  const [sosOptions, setSosOptions] = useState([]);
  const [sosLoading, setSosLoading] = useState(false);
  const [sosChecked, setSosChecked] = useState(false);
  const [selectedSosKey, setSelectedSosKey] = useState("");
  const [siteAutoFilled, setSiteAutoFilled] = useState(false);
  const [unitCodeAutoFilled, setUnitCodeAutoFilled] = useState(false);
  const [materialUuid, setMaterialUuid] = useState(null);
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showProductSuggestions, setShowProductSuggestions] = useState(false);
  // Holistic (every site/warehouse, not just this order's site) BOM
  // Component Stock Status panel (Aug 2026, user's explicit ask) - shown
  // on-demand via the "Check Stock" button next to Create Production
  // Order (not automatic), splitting this card's space in half with the
  // form once open. Required/Shortfall are computed client-side from
  // `bom_qty_per_unit` x whatever Quantity is currently typed, so
  // adjusting Quantity updates the shortage live with no extra API call.
  const [showBomPanel, setShowBomPanel] = useState(false);
  const [bomStockStatus, setBomStockStatus] = useState(null);
  const [bomStockLoading, setBomStockLoading] = useState(false);
  // "Refresh Live SFG Stock" (Aug 2026, user's explicit ask): once a short
  // sub-assembly's own production order is confirmed, pull its updated
  // SFG warehouse stock right away instead of waiting for the scheduled
  // inventory_cache refresh, then retry order creation.
  const [refreshingSfgStock, setRefreshingSfgStock] = useState(false);
  const [refreshSfgElapsed, setRefreshSfgElapsed] = useState(0);
  const [refreshSfgStatus, setRefreshSfgStatus] = useState(null);
  // Every in-flight create-and-release job this browser session knows
  // about, tracked in the "Active Orders" table below - NOT tied to the
  // form, so submitting one order never blocks starting another while the
  // first is still running/paused in the background.
  // Lazily hydrated straight from localStorage during the initial render
  // (not via a post-mount effect) - fixes a real bug where React 18
  // StrictMode's dev-only double-invoke of effects on mount raced the old
  // "restore, then persist" effect pair and clobbered localStorage with
  // "[]" before the restored job ever reached state, silently wiping an
  // in-flight order's tracking (and its Stop button) on every refresh.
  const [activeJobs, setActiveJobs] = useState(() => {
    let stored = [];
    try { stored = JSON.parse(localStorage.getItem(ACTIVE_JOBS_STORAGE_KEY) || "[]"); } catch { stored = []; }
    return stored.map((s) => ({
      ...s, status: "running", elapsedSeconds: Math.round((Date.now() - s.startedAt) / 1000),
      storeRequest: null, deciding: false, expanded: false,
    }));
  });
  const productInputWrapperRef = useRef(null);
  const suggestionDebounceRef = useRef(null);
  const suggestionRequestRef = useRef(null);
  const pollingJobIdsRef = useRef(new Set());
  const [resumingJobId, setResumingJobId] = useState(null);
  const [retryingProposalId, setRetryingProposalId] = useState(null);

  const COMMON_UOM_CODES = ["EA", "KGM", "MTR", "LTR", "PC", "SET", "BOX", "TO"];

  useEffect(() => {
    if (suggestionDebounceRef.current) clearTimeout(suggestionDebounceRef.current);
    const q = materialId.trim();
    if (q.length < 2) {
      setProductSuggestions([]);
      return;
    }
    suggestionDebounceRef.current = setTimeout(async () => {
      const requestId = q;
      suggestionRequestRef.current = requestId;
      try {
        const { data } = await axios.get(`${API}/products/search`, { params: { q, limit: 10 } });
        if (suggestionRequestRef.current === requestId) setProductSuggestions(data);
      } catch {
        if (suggestionRequestRef.current === requestId) setProductSuggestions([]);
      }
    }, 250);
    return () => clearTimeout(suggestionDebounceRef.current);
  }, [materialId]);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (productInputWrapperRef.current && !productInputWrapperRef.current.contains(e.target)) {
        setShowProductSuggestions(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  // selectedSosKey === "" means "nothing picked yet" - Number("") is 0,
  // which would silently resolve to sosOptions[0] and defeat the entire
  // point of forcing an explicit choice when there are 2+ options (a real
  // bug found by testing: a manually-typed Site was enough to submit an
  // order bound to option[0]'s Production Model with nothing ever picked).
  const selectedSosOption = selectedSosKey === "" ? null : sosOptions[Number(selectedSosKey)];

  const chooseSosOption = (key) => {
    setSelectedSosKey(key);
    const option = sosOptions[Number(key)];
    if (option) { setSiteId(option.site_id); setSiteAutoFilled(true); } // model determines site in SAP, not the other way around
    setBomStockStatus(null); // different model may mean a different real BOM - stale panel data would be misleading
  };

  const fetchBomStock = async (live) => {
    const product = materialId.trim();
    if (!product) return;
    setBomStockLoading(true);
    try {
      const { data } = await axios.get(`${API}/production-confirmation/bom-stock-status`, {
        params: {
          main_output_product: product,
          production_model_uuid: selectedSosOption ? selectedSosOption.production_model_uuid : undefined,
          live: !!live,
          // Oct 2026: with a site, the panel also shows how much of that
          // site's stock is already promised to other open orders - the same
          // thing SAP's "Release Feasibility Yellow" gate judges at release.
          site_id: siteId.trim() || undefined,
        },
      });
      setBomStockStatus(data);
    } catch {
      toast.error(live ? "Failed to check live BOM component stock - try again in a moment" : "Failed to load BOM component stock");
    } finally {
      setBomStockLoading(false);
    }
  };

  const openBomStockPanel = () => {
    setShowBomPanel(true);
    setBomStockSearch("");
    setBomStockShortageOnly(false);
    setExpandedBomComponents(new Set());
    if (!bomStockStatus) fetchBomStock(false);
  };

  // Flattened, ready-to-render rows (one per component-location, or one
  // placeholder row for a component with no stock anywhere) - shared by
  // Filterable/searchable/sortable, grouped-by-component list (Aug 2026,
  // user's explicit ask) - shared by both the modal's <table> and the
  // Excel/CSV export below so exporting always matches what's on screen.
  // "Not Assigned" is SAP's default/USABLE stock status, so per the
  // user's explicit ask it's hidden rather than shown as noise - only a
  // real exception status (Inspection, Blocked, etc.) is surfaced.
  const [bomStockSearch, setBomStockSearch] = useState("");
  const [bomStockShortageOnly, setBomStockShortageOnly] = useState(false);
  const [expandedBomComponents, setExpandedBomComponents] = useState(new Set());

  const toggleBomComponentExpand = (productId) => {
    setExpandedBomComponents((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId); else next.add(productId);
      return next;
    });
  };

  const bomStockComponents = useMemo(() => {
    if (!bomStockStatus?.checked) return [];
    const q = bomStockSearch.trim().toLowerCase();
    let list = bomStockStatus.components.map((c) => {
      const requiredQty = Math.round(c.bom_qty_per_unit * (Number(quantity) || 0) * 1e4) / 1e4;
      const shortfall = Math.max(0, Math.round((requiredQty - c.total_usable_qty) * 1e4) / 1e4);
      // Oct 2026: a component can have plenty of stock company-wide and still
      // be unreleasable, because this site's share of it is already promised
      // to other open orders. Both verdicts are kept - "no stock" (red) and
      // "stock exists but is spoken for" (amber) are different problems.
      const freeQty = c.free_qty;
      const committedShort = freeQty != null && requiredQty > 0 && requiredQty > freeQty;
      return {
        ...c, requiredQty, shortfall, sufficient: requiredQty <= 0 || shortfall <= 0,
        committedShort, freeShortfall: committedShort ? Math.round((requiredQty - freeQty) * 1e4) / 1e4 : 0,
      };
    });
    if (q) list = list.filter((c) => c.product_id.toLowerCase().includes(q) || (c.description || "").toLowerCase().includes(q));
    if (bomStockShortageOnly) list = list.filter((c) => !c.sufficient || c.committedShort);
    return [...list].sort((a, b) => {
      if (a.sufficient !== b.sufficient) return a.sufficient ? 1 : -1; // shortages first
      if (a.committedShort !== b.committedShort) return a.committedShort ? -1 : 1; // then "already promised"
      return a.product_id.localeCompare(b.product_id);
    });
  }, [bomStockStatus, quantity, bomStockSearch, bomStockShortageOnly]);

  const downloadBomStockExcel = () => {
    if (bomStockComponents.length === 0) return;
    const withCommitments = !!bomStockStatus?.commitments;
    const rows = [["Component", "Description", "Available", "Required", "Shortfall",
      ...(withCommitments ? [`At ${bomStockStatus.commitments.site_id}`, "Committed", "Free", "Promised To"] : []),
      "Unit", "Site", "Warehouse", "Stock Status", "Location Qty"]];
    bomStockComponents.forEach((c) => {
      const locs = c.locations.length > 0 ? c.locations : [{ site: "", warehouse: "", stock_status: "", qty: "" }];
      locs.forEach((loc) => {
        const isDefaultStatus = (loc.stock_status || "").trim().toLowerCase() === "not assigned";
        rows.push([
          c.product_id, c.description || "", c.total_usable_qty, c.requiredQty, c.shortfall,
          ...(withCommitments ? [
            c.site_usable_qty ?? "", c.committed_qty ?? "", c.free_qty ?? "",
            (c.committed_to || []).map((o) => `${o.production_order_id} (${o.qty})`).join(" | ")
              + ((c.committed_order_count || 0) > (c.committed_to || []).length
                ? ` | +${c.committed_order_count - c.committed_to.length} more` : ""),
          ] : []),
          c.unit_of_measure ? formatUnit(c.unit_of_measure) : "",
          loc.site || "", loc.warehouse || "", isDefaultStatus ? "" : (loc.stock_status || ""), loc.qty ?? "",
        ]);
      });
    });
    const sheet = XLSX.utils.aoa_to_sheet(rows);
    sheet["!cols"] = [{ wch: 16 }, { wch: 28 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
      ...(withCommitments ? [{ wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 30 }] : []),
      { wch: 6 }, { wch: 24 }, { wch: 24 }, { wch: 14 }, { wch: 12 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, sheet, "BOM Stock");
    XLSX.writeFile(workbook, `bom-stock-${materialId.trim() || "component"}-x${quantity || 0}.xlsx`);
  };

  const [lastCheckedId, setLastCheckedId] = useState(null);
  const [sosCheckFailed, setSosCheckFailed] = useState(false);
  const [sosCheckFailReason, setSosCheckFailReason] = useState(null);

  const checkSourceOfSupply = async (idOverride) => {
    const id = (idOverride ?? materialId).trim();
    if (!id || (id === lastCheckedId && !sosCheckFailed)) return;  // avoid redundant re-lookups (e.g. blur right before a submit click) that would reset materialUuid/site mid-flow - unless the last attempt failed and this is a retry
    setMaterialId(id);
    setLastCheckedId(id);
    setShowProductSuggestions(false);
    setSosLoading(true);
    setSosCheckFailed(false);
    setSosCheckFailReason(null);
    setSosOptions([]);
    setSelectedSosKey("");
    setMaterialUuid(null);
    try {
      const { data } = await axios.get(`${API}/production-confirmation/source-of-supply-options/${encodeURIComponent(id)}`);
      setMaterialUuid(data.material_uuid || null);
      const options = data.options || [];
      setSosOptions(options);
      // Only auto-pick when there's exactly ONE valid Model/Site combo -
      // no real ambiguity there. With 2+ valid options, force the user to
      // explicitly choose (previously defaulted to the first "Active" one
      // alphabetically, which could silently pick a model whose BOM has a
      // stock shortage while a perfectly fine alternative sat unused in
      // the dropdown - user's explicit call, no more auto-pick when there's
      // a real choice to make).
      if (options.length === 1) {
        setSelectedSosKey("0");
        setSiteId(options[0].site_id); // model determines site - always overwritten here, never an independent pre-filter
        setSiteAutoFilled(true);
      } else if (siteAutoFilled) {
        setSiteId(""); // clear a stale auto-filled site from a previous material - but never clobber a site the user typed themselves
        setSiteAutoFilled(false);
      }
      // Base UoM (SAP's real base unit for this material, exposed Aug
      // 2026) - lock the picker to it, same UX pattern as Site locking
      // from the chosen Production Model, so the previously-free UoM
      // choice can no longer silently mismatch SAP's own unit.
      if (data.base_uom) {
        setUnitCode(data.base_uom);
        setUnitCodeAutoFilled(true);
      } else if (unitCodeAutoFilled) {
        setUnitCode("EA");
        setUnitCodeAutoFilled(false);
      }
    } catch (e) {
      // A transient network/5xx error is NOT the same as "material doesn't
      // exist" - don't show the red not-recognized warning or block submit
      // for a real product just because one lookup attempt failed.
      // Oct 3 2026: when the backend knows WHY (e.g. SAP refused its OData
      // login), show that instead of guessing "network hiccup" - a wrong
      // explanation sent the user hunting for a Product ID problem that
      // never existed.
      setSosOptions([]);
      setSosCheckFailed(true);
      setSosCheckFailReason(e?.response?.data?.detail || null);
    } finally {
      setSosLoading(false);
      setSosChecked(true);
    }
  };

  const loadHistory = useCallback(() => {
    setLoadingHistory(true);
    axios.get(`${API}/production-confirmation/proposal-history`, {
      params: { start_date: historyStartDate || undefined, end_date: historyEndDate || undefined },
    }).then(({ data }) => setHistory(data.entries)).catch(() => toast.error("Failed to load Proposal/Release history")).finally(() => setLoadingHistory(false));
  }, [historyStartDate, historyEndDate]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // "Result" for filtering - mirrors the exact badge logic already
  // rendered per-row below (Released/Failed/Release Failed/Created/
  // In Progress) so the dropdown options match what's actually shown.
  const historyResultOf = (h) => {
    if (h.type !== "proposal_created") return h.success ? "released" : "failed";
    if (h.production_order_id) return h.released ? "released" : "failed";
    return h.can_retry ? "created" : "in_progress";
  };

  const filteredHistory = useMemo(() => {
    return history.filter((h) => {
      if (historyResultFilter !== "all" && historyResultOf(h) !== historyResultFilter) return false;
      if (historyModelFilter.trim() && !(h.production_model_id || "").toLowerCase().includes(historyModelFilter.trim().toLowerCase())) return false;
      if (historyProductFilter.trim() && !(h.material_id || "").toLowerCase().includes(historyProductFilter.trim().toLowerCase())) return false;
      return true;
    });
  }, [history, historyResultFilter, historyModelFilter, historyProductFilter]);

  const removeActiveJob = (jobId) => setActiveJobs((prev) => prev.filter((j) => j.job_id !== jobId));
  const toggleJobExpand = (jobId) => setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, expanded: !j.expanded } : j)));

  // "Resume" action (Aug 27 2026, user's explicit ask) on a failed order
  // job that already has a known SAP Proposal ID - hands that ID + the
  // original form snapshot back to the backend, which resumes the
  // Proposal -> Order -> Release pipeline under a fresh job_id instead of
  // requiring a trip to the SAP UI to finish it by hand.
  const resumeFailedJob = async (job) => {
    setResumingJobId(job.job_id);
    try {
      const { data } = await axios.post(`${API}/production-confirmation/create-and-release-order/${job.job_id}/resume`, {
        actor: actorName.trim(),
      });
      removeActiveJob(job.job_id);
      setActiveJobs((prev) => [...prev, {
        job_id: data.job_id, material_id: job.material_id, site_id: job.site_id,
        quantity: job.quantity, unit_code: job.unit_code, status: "waiting_for_order",
        startedAt: Date.now(), elapsedSeconds: 0,
      }]);
      pollJob(data.job_id);
      toast.success(`Resuming from Proposal ${data.production_proposal_id} - no need to use the SAP UI`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to resume order creation");
    } finally {
      setResumingJobId(null);
    }
  };

  // "Retry" (Aug 28 2026, user's explicit ask - "if proposal created ex:
  // 225857 is it possible i can retry to create production order of this
  // request") on a "Created"-only row in the permanent History table
  // below (a Proposal made in SAP that never became an Order) - unlike
  // Resume above, this works even long after the original job doc has
  // expired (job_store's 24h TTL) since it's sourced straight from the
  // History row's own saved fields, not a live job.
  const retryFromProposal = async (h) => {
    setRetryingProposalId(h.production_proposal_id);
    try {
      const { data } = await axios.post(`${API}/production-confirmation/retry-from-proposal`, {
        production_proposal_id: h.production_proposal_id, material_id: h.material_id, site_id: h.site_id,
        quantity: h.quantity, unit_code: h.unit_code || "EA", actor: actorName.trim(),
      });
      setActiveJobs((prev) => [...prev, {
        job_id: data.job_id, material_id: h.material_id, site_id: h.site_id,
        quantity: h.quantity, unit_code: h.unit_code || "EA", status: "waiting_for_order",
        startedAt: Date.now(), elapsedSeconds: 0,
      }]);
      pollJob(data.job_id);
      toast.success(`Retrying from Proposal ${data.production_proposal_id} - track it in Active Orders above`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry order creation");
    } finally {
      setRetryingProposalId(null);
    }
  };

  // "Retry Now" (Aug 2026, user's explicit ask) - lets an impatient user
  // fire the Release trigger immediately instead of waiting out the
  // background job's own ~90s retrigger cycle. The background loop keeps
  // running/re-triggering on its own schedule regardless of this call's
  // outcome, so this can never make things worse.
  const forceRetrigger = async (jobId) => {
    setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, retrying: true } : j)));
    try {
      const { data } = await axios.post(`${API}/production-confirmation/create-and-release-order/${jobId}/force-retrigger`);
      if (data.success) toast.success("SAP accepted the retry - still watching for the resulting Order");
      else toast.error(data.error || "SAP rejected the retry - it will keep auto-retrying in the background");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to retry");
    } finally {
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, retrying: false } : j)));
    }
  };

  // Runs entirely independently per job - multiple can be in flight at
  // once, each polling its own status on its own timer, none of them
  // blocking the form above from starting yet another order.
  const pollJob = useCallback((jobId) => {
    if (pollingJobIdsRef.current.has(jobId)) return;
    pollingJobIdsRef.current.add(jobId);
    const tickTimer = setInterval(() => {
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, elapsedSeconds: Math.round((Date.now() - j.startedAt) / 1000) } : j)));
    }, 1000);
    let lastStoreReqKey = null;
    (async () => {
      try {
        // eslint-disable-next-line no-constant-condition
        while (true) {
          await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
          let job;
          try {
            ({ data: job } = await axios.get(`${API}/production-confirmation/create-and-release-order/status/${jobId}`));
          } catch (e) {
            if (e.response?.status === 404) {
              // Real bug reproduced live (Aug 2026): a genuinely-gone job
              // (e.g. a stale localStorage entry from a much older
              // session whose Mongo doc no longer exists) used to be
              // treated the same as a transient network hiccup below,
              // so this loop retried FOREVER and the row's spinner never
              // stopped, no matter what the user clicked. A 404 here is
              // permanent, not transient - stop tracking it for good.
              toast.error(`Lost track of this order (job no longer exists) - it may already be done; check History`);
              removeActiveJob(jobId);
              break;
            }
            continue; // transient network hiccup - keep tracking this job, try again next round
          }
          if (PAUSED_STATUSES.includes(job.status)) {
            const key = `${job.store_request_id}:${job.status}`;
            if (job.store_request_id && lastStoreReqKey !== key) {
              try {
                const { data: reqDoc } = await axios.get(`${API}/production-confirmation/store-requests/by-job/${jobId}`);
                lastStoreReqKey = key;
                setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: job.status, progress: job.progress || j.progress, storeRequest: reqDoc, sfgShortage: job.sfg_shortage || j.sfgShortage, commitmentWarning: job.commitment_warning || j.commitmentWarning } : j)));
              } catch {
                setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: job.status, progress: job.progress || j.progress, sfgShortage: job.sfg_shortage || j.sfgShortage, commitmentWarning: job.commitment_warning || j.commitmentWarning } : j)));
              }
            } else {
              setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: job.status, progress: job.progress || j.progress, sfgShortage: job.sfg_shortage || j.sfgShortage, commitmentWarning: job.commitment_warning || j.commitmentWarning } : j)));
            }
            continue;
          }
          setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? {
            ...j, status: job.status,
            progress: job.progress || j.progress,
            // Re-base the local clock on the server's own measurement, so a
            // reload, a second device or a slow submit can never make it lie.
            startedAt: job.progress?.elapsed_seconds != null ? Date.now() - job.progress.elapsed_seconds * 1000 : j.startedAt,
            productionProposalId: job.production_proposal_id || j.productionProposalId,
            productionOrderId: job.production_order_id || j.productionOrderId,
            orderReleaseState: job.order_release_state,
            orderReleaseAttempts: job.order_release_attempts,
            orderReleaseError: job.order_release_error,
            nextReleaseAttemptAt: job.next_release_attempt_at,
            releaseTriggerCount: job.release_trigger_count,
            lastReleaseTriggerOk: job.last_release_trigger_ok,
            lastReleaseTriggerError: job.last_release_trigger_error,
            proposalStatus: job.proposal_status || j.proposalStatus,
            sfgShortage: job.sfg_shortage || j.sfgShortage, commitmentWarning: job.commitment_warning || j.commitmentWarning,
          } : j)));
          if (job.status === "done") {
            const result = job.result;
            if (result.production_order_id && result.released) {
              toast.success(`Production Order ${result.production_order_id} created and released in SAP - ready for confirmation`);
            } else if (result.production_order_id) {
              // Oct 2026: SAP creates the Order but refuses to RELEASE it
              // when its Production Model disallows release on an infeasible
              // component ("free stock does not cover all open demands").
              // That needs to stay on screen with the real reason and the
              // orders holding the stock - not a toast that vanishes.
              toast.error(`Order ${result.production_order_id} was created but SAP did not release it`);
              setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? {
                ...j, status: "failed", error: result.release_blocked?.message || "SAP created the Order but Release did not go through.",
                failure: { ...result, reason: result.release_blocked?.reason || "not_released" },
              } : j)));
              loadHistory();
              break;
            } else {
              toast.error(result.note || "Proposal created but the Order hasn't appeared yet - it keeps retrying automatically, check history shortly");
            }
            removeActiveJob(jobId);
            loadHistory();
            break;
          }
          if (job.status === "paused_sap_block") {
            // SAP refused this Proposal/Order for a master-data reason (e.g.
            // the item's Valuation not Active at that site, which SAP words
            // as "invalid REPM"). Keep the row on screen with SAP's own
            // wording + a Retry, instead of a vanishing toast.
            const block = job.result?.sap_block || {};
            setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? {
              ...j, status: "paused_sap_block", error: block.message || "SAP refused to release this order.",
              failure: job.result, sapError: block.sap_error,
            } : j)));
            toast.error("SAP refused this order - fix it in SAP, then Retry", { duration: 8000 });
            loadHistory();
            break;
          }
          if (job.status === "cancelled") {
            toast.error(job.result?.note || "Order creation was cancelled after the store approval decision");
            removeActiveJob(jobId);
            loadHistory();
            break;
          }
          if (job.status === "failed") {
            // SFG-shortage block (Aug 2026, user's explicit ask): keep this
            // row visible with a persistent, dismissible detail table
            // instead of a toast that vanishes in a few seconds - a real
            // "which component, how short" needs to stay on screen until
            // the planner has acted on it.
            if (job.result?.reason === "sfg_shortage") {
              setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: "failed", failure: job.result, error: job.error } : j)));
              break;
            }
            // Aug 27 2026 fix (user's explicit ask - "if an error happens
            // during production order creation ... it would be ideal to
            // have a recovery on app frontend"): any OTHER mid-pipeline
            // failure used to just toast + vanish, forcing a trip to the
            // SAP UI to find and finish a Proposal that may already
            // exist there. Now it stays on screen with the real reason
            // and a "Resume" action when a Proposal ID is known.
            setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, status: "failed", failure: job.result, error: job.error } : j)));
            break;
          }
        }
      } finally {
        clearInterval(tickTimer);
        pollingJobIdsRef.current.delete(jobId);
      }
    })();
  }, [loadHistory]);

  // Persist the (small) seed info for every active job so a page refresh
  // doesn't lose track of orders still running in the background. Safe to
  // run on every render including the very first (it just re-writes back
  // the same data `activeJobs` was hydrated from above) - no skip-guard
  // needed now that hydration itself happens synchronously in useState.
  useEffect(() => {
    const seed = activeJobs.map(({ job_id, material_id, site_id, quantity, unit_code, startedAt }) => ({ job_id, material_id, site_id, quantity, unit_code, startedAt }));
    localStorage.setItem(ACTIVE_JOBS_STORAGE_KEY, JSON.stringify(seed));
  }, [activeJobs]);

  // Kick off polling once for whatever was hydrated above - pollJob's own
  // pollingJobIdsRef guard makes this safe even under StrictMode's dev
  // double-invoke of mount effects.
  useEffect(() => {
    activeJobs.forEach((j) => pollJob(j.job_id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!refreshingSfgStock) {
      setRefreshSfgElapsed(0);
      return;
    }
    const interval = setInterval(() => setRefreshSfgElapsed((s) => s + 1), 1000);
    return () => clearInterval(interval);
  }, [refreshingSfgStock]);

  const refreshLiveSfgStock = async (siteOverride) => {
    const site = (siteOverride || siteId).trim();
    if (!site) {
      toast.error("Enter a Site first");
      return;
    }
    setRefreshingSfgStock(true);
    setRefreshSfgStatus(`Pulling live SFG stock for ${site} from SAP...`);
    try {
      const { data } = await axios.post(`${API}/production-confirmation/refresh-live-sfg-stock`, null, { params: { site_id: site } });
      let job = null;
      for (let i = 0; i < 30; i++) {
        await new Promise((r) => setTimeout(r, 1000));
        const res = await axios.get(`${API}/production-confirmation/refresh-live-sfg-stock/${data.job_id}`);
        job = res.data;
        if (job.status === "done" || job.status === "failed") break;
      }
      if (job?.status === "done") {
        toast.success(`Live SFG stock refreshed for ${site} - retry creating the order now`);
      } else {
        toast.error(job?.error || "Failed to refresh live SFG stock");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to refresh live SFG stock");
    } finally {
      setRefreshingSfgStock(false);
      setRefreshSfgStatus(null);
    }
  };

  const createProposal = async () => {
    if (sosOptions.length > 1 && !selectedSosOption) {
      toast.error("This material has multiple valid Production Models - pick one from the Source of Supply list before creating the order");
      return;
    }
    if (!materialId.trim() || !siteId.trim() || !quantity) {
      toast.error("Product, Site and Quantity are required");
      return;
    }
    if (Number(quantity) <= 0) {
      toast.error("Quantity must be greater than zero");
      return;
    }
    if (sosLoading) {
      toast.error("Still checking this Product ID with SAP - wait a moment and try again");
      return;
    }
    if (sosChecked && !materialUuid && !sosCheckFailed) {
      toast.error("Product ID not recognized in SAP - pick one from the suggestions or check the spelling");
      return;
    }
    setSubmitting(true);
    setValuationBlock(null);
    try {
      const payloadMaterialId = materialId.trim();
      const payloadSiteId = siteId.trim().toUpperCase();
      const payloadQuantity = Number(quantity);
      const payloadUnitCode = unitCode.trim().toUpperCase() || "EA";
      const { data } = await axios.post(`${API}/production-confirmation/create-and-release-order`, {
        material_id: payloadMaterialId,
        site_id: payloadSiteId,
        quantity: payloadQuantity,
        unit_code: payloadUnitCode,
        availability_datetime: requestedEndDate ? new Date(requestedEndDate).toISOString() : null,
        actor: actorName.trim(),
        logistic_relationship_uuid: selectedSosOption ? selectedSosOption.logistic_relationship_uuid : null,
        production_model_uuid: selectedSosOption ? selectedSosOption.production_model_uuid : null,
        production_model_id: selectedSosOption ? selectedSosOption.production_model_id : null,
      });
      const jobId = data.job_id;
      setValuationBlock(data.valuation_note ? { kind: "warning", message: data.valuation_note } : null);
      setActiveJobs((prev) => [{
        job_id: jobId, material_id: payloadMaterialId, site_id: payloadSiteId, quantity: payloadQuantity, unit_code: payloadUnitCode,
        status: "checking_stock", startedAt: Date.now(), elapsedSeconds: 0, storeRequest: null, deciding: false, expanded: false,
      }, ...prev]);
      pollJob(jobId);
      toast.success("Order request submitted - track its progress in the Active Orders table below.");
      // Free up the form immediately so another order can be submitted
      // right away - it no longer waits for this one to finish/pause.
      setMaterialId(""); setQuantity("1"); setRequestedEndDate(""); setSiteId("");
      setSosOptions([]); setSosChecked(false); setSelectedSosKey(""); setSiteAutoFilled(false);
      setUnitCode("EA"); setUnitCodeAutoFilled(false); setMaterialUuid(null); setLastCheckedId(null);
      setShowBomPanel(false); setBomStockStatus(null);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Failed to create Production Order in SAP";
      if (e.response?.status === 400) setValuationBlock({ kind: "error", message: detail });
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const decideStoreRequest = async (jobId, requestId, decision) => {
    if (!requestId) return;
    setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, deciding: true } : j)));
    try {
      await axios.post(`${API}/production-confirmation/store-requests/${requestId}/decision`, {
        decision, actor: actorName.trim(),
      });
      toast.success(decision === "approve" ? "Approved - resuming automated order creation" : "Rejected - order creation cancelled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record your decision");
    } finally {
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, deciding: false } : j)));
    }
  };

  // "Stop the rotating wheel" (Aug 2026): stops THIS app's own polling for
  // the resulting Order - it can never delete/cancel the SAP Proposal
  // already created (no such SAP API exists), so that stays exactly as
  // it is, un-converted, in SAP. pollJob's existing "cancelled" handling
  // toasts + removes the row once the backend confirms the stop.
  const stopTrackingJob = async (jobId) => {
    setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, stopping: true } : j)));
    try {
      await axios.post(`${API}/production-confirmation/create-and-release-order/${jobId}/cancel`);
      // The backend only checks this flag at specific checkpoints (stock
      // check, proposal creation, the order/release poll loop) - give it
      // a reasonable window to land before re-enabling the button, so a
      // Stop clicked at an awkward moment doesn't leave "Stopping..."
      // disabled forever with no feedback (pollJob's own "cancelled"
      // handling removes the row well before this if it lands sooner).
      setTimeout(() => {
        setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId && j.stopping ? { ...j, stopping: false } : j)));
      }, 30000);
    } catch (e) {
      if (e.response?.status === 404) {
        // Same stale-job case as pollJob's 404 handling above - nothing
        // to actually stop, so just drop it from the list instead of
        // leaving the row spinning forever on a job that's already gone.
        toast.error("This order no longer exists - removing it from the list");
        removeActiveJob(jobId);
        return;
      }
      toast.error(e.response?.data?.detail || "Failed to stop this order run");
      setActiveJobs((prev) => prev.map((j) => (j.job_id === jobId ? { ...j, stopping: false } : j)));
    }
  };

  const releaseOrder = async () => {
    if (!releaseOrderId.trim()) {
      toast.error("Enter the Production Order ID to release");
      return;
    }
    setReleasing(true);
    try {
      await axios.post(`${API}/production-confirmation/release-order`, {
        production_order_id: releaseOrderId.trim(),
        actor: actorName.trim(),
      });
      toast.success(`Production Order ${releaseOrderId.trim()} released in SAP`);
      setReleaseOrderId("");
      loadHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to release Production Order in SAP");
    } finally {
      setReleasing(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3" data-testid="create-proposal-card">
          <h3 className="font-heading text-sm font-bold text-[#1D2939] uppercase tracking-wide">Create Production Order</h3>
          <p className="text-xs text-[#667085]">Creates and releases a Production Order in SAP - fully automated, retries in the background until SAP converts it. Checks component stock first; submitting an order tracks it in the Active Orders table below so this form stays free to submit another right away.</p>
          <div ref={productInputWrapperRef} className="relative">
            <Label className="text-xs font-bold text-[#344054]">Product ID</Label>
            <Input
              value={materialId}
              onChange={(e) => { setMaterialId(e.target.value); setSosChecked(false); setSosOptions([]); setMaterialUuid(null); setLastCheckedId(null); setShowProductSuggestions(true); setBomStockStatus(null); }}
              onFocus={() => setShowProductSuggestions(true)}
              onBlur={() => checkSourceOfSupply()}
              placeholder="e.g. MAZ42117272-TA"
              data-testid="create-proposal-product-input"
              autoComplete="off"
            />
            {showProductSuggestions && productSuggestions.length > 0 && (
              <div className="absolute z-10 mt-1 w-full bg-white border border-[#D0D5DD] rounded-sm shadow-md max-h-56 overflow-y-auto" data-testid="product-suggestions-dropdown">
                {productSuggestions.map((s) => (
                  <button
                    key={s.product_id}
                    type="button"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-[#F9FAFB] border-b border-[#EAECF0] last:border-0"
                    onMouseDown={(e) => { e.preventDefault(); checkSourceOfSupply(s.product_id); }}
                    data-testid={`product-suggestion-${s.product_id}`}
                  >
                    <div className="font-medium text-[#1D2939]">{s.product_id}</div>
                    {s.description && <div className="text-xs text-[#667085]">{s.description}</div>}
                  </button>
                ))}
              </div>
            )}
            {sosChecked && !sosLoading && !materialUuid && !sosCheckFailed && (
              <p className="text-xs text-[#B42318] mt-1" data-testid="product-not-recognized-warning">
                Product ID not recognized in SAP - pick a suggestion above or check the spelling.
              </p>
            )}
            {sosChecked && !sosLoading && materialUuid && sosOptions.length === 0 && (
              <p className="text-xs text-[#B54708] mt-1" data-testid="no-production-model-warning">
                Product recognized in SAP, but no Production Model/BOM is set up for it yet at any Site - set one up in SAP first, or type a Site manually below if you know one already exists.
              </p>
            )}
            {sosCheckFailed && (
              <p className="text-xs text-[#B54708] mt-1" data-testid="product-lookup-failed-warning">
                {sosCheckFailReason || "Could not verify this Product ID with SAP just now (network hiccup) - you can still proceed, or blur the field again to retry."}
              </p>
            )}
          </div>
          <div className="min-h-[26px]">
            {sosLoading && <p className="text-xs text-[#667085]" data-testid="sos-loading-text">Checking available Production Models...</p>}
            {sosChecked && sosOptions.length > 0 && (
              <div data-testid="source-of-supply-picker">
                <Label className="text-xs font-bold text-[#344054]">Source of Supply (Production Model)</Label>
                <Select value={selectedSosKey} onValueChange={chooseSosOption}>
                  <SelectTrigger data-testid="source-of-supply-select-trigger">
                    <SelectValue placeholder="Choose a Production Model" />
                  </SelectTrigger>
                  <SelectContent>
                    {sosOptions.map((o, idx) => (
                      <SelectItem key={`${o.production_model_id}-${o.site_id}`} value={String(idx)} data-testid={`source-of-supply-option-${o.production_model_id}-${o.site_id}`}>
                        {o.production_model_id}{o.description ? ` — ${o.description}` : ""} (Site {o.site_id}){o.is_active ? "" : " (Obsolete)"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p
                  className={`text-xs mt-1 ${sosOptions.length > 1 && !selectedSosOption ? "font-bold text-[#B54708]" : "text-[#667085]"}`}
                  data-testid={sosOptions.length > 1 && !selectedSosOption ? "sos-choice-required-warning" : undefined}
                >
                  {sosOptions.length > 1
                    ? selectedSosOption
                      ? `Using ${selectedSosOption.production_model_id} (Site ${selectedSosOption.site_id}).`
                      : `This material has ${sosOptions.length} valid Production Model/Site combinations - you must pick one before creating the order (it sets the Site for you; not auto-picked since one option may be short on stock while another isn't).`
                    : "Picking this confirms the Production Model - and its Site - SAP will use."}
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#344054]">Site</Label>
              <Input
                value={siteId}
                onChange={(e) => { setSiteId(e.target.value.toUpperCase()); setSiteAutoFilled(false); setBomStockStatus(null); }}
                placeholder="e.g. P2"
                disabled={siteAutoFilled}
                data-testid="create-proposal-site-input"
              />
              {siteAutoFilled && <p className="text-[11px] text-[#667085] mt-0.5">Locked - set by the chosen Production Model above</p>}
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={refreshingSfgStock || !siteId.trim()}
                onClick={() => refreshLiveSfgStock()}
                className="mt-1.5 h-7 text-[11px]"
                data-testid="refresh-live-sfg-stock-button"
              >
                <ArrowClockwise size={12} className={`mr-1 ${refreshingSfgStock ? "animate-spin" : ""}`} />
                {refreshingSfgStock ? `Refreshing (${refreshSfgElapsed}s)...` : "Refresh Live SFG Stock"}
              </Button>
              {refreshingSfgStock && refreshSfgStatus && (
                <p className="text-[11px] text-[#667085] mt-0.5" data-testid="refresh-live-sfg-stock-status">{refreshSfgStatus}</p>
              )}
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">UoM</Label>
              <Select value={unitCode} onValueChange={setUnitCode} disabled={unitCodeAutoFilled}>
                <SelectTrigger data-testid="create-proposal-uom-select-trigger">
                  <SelectValue placeholder="EA" />
                </SelectTrigger>
                <SelectContent>
                  {(COMMON_UOM_CODES.includes(unitCode) ? COMMON_UOM_CODES : [unitCode, ...COMMON_UOM_CODES]).map((code) => (
                    <SelectItem key={code} value={code} data-testid={`create-proposal-uom-option-${code}`}>{code}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {unitCodeAutoFilled ? (
                <p className="text-[11px] text-[#667085] mt-0.5" data-testid="uom-locked-helper">Locked - SAP's base unit for this material</p>
              ) : unitCode !== "EA" && (
                <p className="text-[11px] text-[#B54708] mt-0.5" data-testid="uom-not-ea-warning">
                  Not verified against SAP's base unit for this material yet - double-check it's correct before submitting.
                </p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-bold text-[#344054]">Quantity</Label>
              <Input type="number" min="0.01" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value)} data-testid="create-proposal-qty-input" />
            </div>
            <div>
              <Label className="text-xs font-bold text-[#344054]">Requested End Date</Label>
              <Input type="date" value={requestedEndDate} onChange={(e) => setRequestedEndDate(e.target.value)} data-testid="create-proposal-date-input" />
            </div>
          </div>
          {valuationBlock && (
            <Alert
              className={valuationBlock.kind === "error" ? "rounded-sm bg-[#FEF3F2] border-[#FECDCA]" : "rounded-sm bg-[#FFFAEB] border-[#FEDF89]"}
              data-testid="valuation-check-banner"
            >
              <WarningCircle size={16} className={valuationBlock.kind === "error" ? "text-[#B42318]" : "text-[#B54708]"} />
              <AlertDescription className={`text-xs ${valuationBlock.kind === "error" ? "text-[#B42318]" : "text-[#B54708]"}`}>
                {valuationBlock.message}
              </AlertDescription>
            </Alert>
          )}
          <div className="flex gap-2">
            <Button onClick={createProposal} disabled={submitting} className="flex-1" data-testid="create-proposal-submit-button">
              {submitting ? "Submitting..." : "Create Production Order"}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!materialId.trim()}
              onClick={openBomStockPanel}
              data-testid="toggle-bom-stock-panel-button"
            >
              Check Stock
            </Button>
          </div>
        </div>

        <div className="bg-white border border-[#D0D5DD] rounded-sm p-4 space-y-3" data-testid="release-order-card">
          <h3 className="font-heading text-sm font-bold text-[#1D2939] uppercase tracking-wide">Fallback: Release Order Manually</h3>
          <p className="text-xs text-[#667085]">Only needed if the automatic flow above times out. Enter a Production Order ID (once visible in SAP) to release it.</p>
          <div>
            <Label className="text-xs font-bold text-[#344054]">Production Order ID</Label>
            <Input value={releaseOrderId} onChange={(e) => setReleaseOrderId(e.target.value)} placeholder="e.g. 69843" data-testid="release-order-id-input" />
          </div>
          <Button onClick={releaseOrder} disabled={releasing} className="w-full" data-testid="release-order-submit-button">
            {releasing ? "Releasing in SAP..." : "Release Production Order"}
          </Button>
        </div>
      </div>

      <Dialog open={showBomPanel} onOpenChange={(open) => setShowBomPanel(open)}>
        <DialogContent className={`${bomStockStatus?.commitments ? "max-w-6xl" : "max-w-4xl"} max-h-[85vh] overflow-hidden flex flex-col`} data-testid="bom-stock-status-panel">
          <DialogHeader>
            <DialogTitle>BOM Component Stock (x{quantity || 0} {formatUnit(unitCode)})</DialogTitle>
            <DialogDescription>
              {materialId.trim()} - every site/warehouse this BOM's components sit in, scaled to the Quantity you entered.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            {bomStockStatus && (
              <span className="text-xs text-[#667085]" data-testid="bom-stock-status-source">
                Source: {bomStockStatus.source === "live" ? "Live SAP" : "Cached snapshot"}{bomStockStatus.fetched_at ? ` · as of ${new Date(bomStockStatus.fetched_at).toLocaleString("en-IN")}` : ""}
                {bomStockStatus.bom_id ? ` · BOM ${bomStockStatus.bom_id}` : ""}
              </span>
            )}
            {bomStockStatus?.superseded_bom_id && (
              <span className="text-xs text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-2 py-1 w-full" data-testid="bom-stock-superseded-note">
                The production model in SAP still points at BOM <strong>{bomStockStatus.superseded_bom_id}</strong>, which SAP itself marks
                inconsistent - these components come from <strong>{bomStockStatus.bom_id}</strong>, the current BOM for this product.
                Re-link the model to it in SAP so the production order consumes the right input.
              </span>
            )}
            {bomStockStatus?.commitments && (
              <span className="text-xs text-[#5925DC] w-full" data-testid="bom-stock-commitments-note">
                <strong>Committed</strong> = still unconfirmed on other open orders at {bomStockStatus.commitments.site_id} (lots created in the last {bomStockStatus.commitments.lookback_days} days,
                {" "}{bomStockStatus.commitments.open_lots_in_window} of {bomStockStatus.commitments.open_lots_total} open lots). <strong>Free</strong> = that site's usable stock minus those commitments - this is what SAP judges when a Production Model disallows release on infeasible components.
                {bomStockStatus.commitments.scan_failed ? " (SAP scan failed - showing the last known figures.)" : ""}
                {bomStockStatus.commitments.stale_cache ? " (Commitment snapshot is a few minutes old - refreshing in the background; reopen for the newest figures.)" : ""}
              </span>
            )}
            <div className="flex gap-2 ml-auto">
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={bomStockLoading || !materialId.trim()}
                onClick={() => fetchBomStock(true)}
                data-testid="bom-stock-status-refresh-button"
              >
                <ArrowClockwise size={12} className={`mr-1.5 ${bomStockLoading ? "animate-spin" : ""}`} />
                {bomStockLoading ? "Checking..." : "Check Live Stock"}
              </Button>
              <Button
                type="button"
                size="sm"
                variant="outline"
                disabled={bomStockComponents.length === 0}
                onClick={downloadBomStockExcel}
                data-testid="bom-stock-status-download-button"
              >
                Download Excel
              </Button>
            </div>
          </div>

          {bomStockLoading && !bomStockStatus && (
            <p className="text-sm text-[#667085]" data-testid="bom-stock-status-loading">Checking BOM component stock...</p>
          )}

          {bomStockStatus?.checked && (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative flex-1 min-w-[200px]">
                  <MagnifyingGlass size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#98A2B3]" />
                  <Input
                    value={bomStockSearch}
                    onChange={(e) => setBomStockSearch(e.target.value)}
                    placeholder="Search by component ID or description..."
                    className="h-8 pl-7 text-xs"
                    data-testid="bom-stock-search-input"
                  />
                </div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-[#344054] cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={bomStockShortageOnly}
                    onChange={(e) => setBomStockShortageOnly(e.target.checked)}
                    data-testid="bom-stock-shortage-only-toggle"
                  />
                  Shortages only
                </label>
              </div>

              <div className="flex-1 overflow-auto border border-[#EAECF0] rounded-sm">
                <table className="w-full text-xs border-collapse" data-testid="bom-stock-status-table">
                  <thead className="sticky top-0">
                    <tr>
                      {["Component", "Description", "Available", "Required", "Shortfall", ...(bomStockStatus.commitments ? [`At ${bomStockStatus.commitments.site_id}`, "Committed", "Free"] : []), "Site", "Warehouse", "Stock Status", "Location Qty"].map((h) => (
                        <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {bomStockComponents.map((c) => {
                      const expanded = expandedBomComponents.has(c.product_id);
                      const hasLocations = c.locations.length > 0;
                      return (
                        <Fragment key={c.product_id}>
                          <tr className={!c.sufficient ? "bg-[#FEF3F2]" : c.committedShort ? "bg-[#F4F3FF]" : "bg-white"} data-testid={`bom-stock-component-row-${c.product_id}`}>
                            <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">
                              <button
                                type="button"
                                className="flex items-center gap-1 disabled:cursor-default"
                                disabled={!hasLocations}
                                onClick={() => toggleBomComponentExpand(c.product_id)}
                                data-testid={`bom-stock-expand-${c.product_id}`}
                              >
                                {hasLocations ? (expanded ? <CaretDown size={11} className="shrink-0" /> : <CaretRight size={11} className="shrink-0" />) : <span className="inline-block w-[11px]" />}
                                {c.product_id}
                              </button>
                            </td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5">{c.description || "—"}</td>
                            <td className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${c.sufficient ? "text-[#027A48]" : "text-[#B42318] font-bold"}`}>
                              {formatQty(c.total_usable_qty)} {formatUnit(c.unit_of_measure)}
                            </td>
                            <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(c.requiredQty)} {formatUnit(c.unit_of_measure)}</td>
                            <td className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${!c.sufficient ? "text-[#B42318] font-bold" : ""}`}>
                              {c.sufficient ? "—" : `${formatQty(c.shortfall)} ${formatUnit(c.unit_of_measure)}`}
                            </td>
                            {bomStockStatus.commitments && (
                              <>
                                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums" data-testid={`bom-stock-site-qty-${c.product_id}`}>
                                  {c.site_usable_qty == null ? "—" : `${formatQty(c.site_usable_qty)} ${formatUnit(c.unit_of_measure)}`}
                                </td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums" data-testid={`bom-stock-committed-${c.product_id}`}>
                                  {c.committed_qty ? `${formatQty(c.committed_qty)} ${formatUnit(c.unit_of_measure)}` : "—"}
                                  {c.committed_lot_count ? <span className="text-[#667085]"> · {c.committed_lot_count} order{c.committed_lot_count > 1 ? "s" : ""}</span> : null}
                                </td>
                                <td className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums ${c.committedShort ? "text-[#5925DC] font-bold" : ""}`} data-testid={`bom-stock-free-${c.product_id}`}>
                                  {c.free_qty == null ? "—" : `${formatQty(c.free_qty)} ${formatUnit(c.unit_of_measure)}`}
                                </td>
                              </>
                            )}
                            <td colSpan={4} className="border border-[#D0D5DD] px-2 py-1.5 text-[#667085]">
                              {c.committedShort && (
                                <span className="text-[#5925DC] font-medium mr-2" data-testid={`bom-stock-committed-note-${c.product_id}`}>
                                  Short by {formatQty(c.freeShortfall)} {formatUnit(c.unit_of_measure)} after commitments ({(c.committed_to || []).slice(0, 3).map((o) => o.production_order_id).join(", ") || "other orders"}{(c.committed_order_count || 0) > 3 ? ` +${c.committed_order_count - 3} more` : ""}) ·
                                </span>
                              )}
                              {hasLocations ? `${c.locations.length} location${c.locations.length > 1 ? "s" : ""} - click to ${expanded ? "collapse" : "expand"}` : "No stock found anywhere"}
                            </td>
                          </tr>
                          {expanded && c.locations.map((loc, li) => {
                            const isDefaultStatus = (loc.stock_status || "").trim().toLowerCase() === "not assigned";
                            return (
                              <tr key={li} className="bg-[#F9FAFB]" data-testid={`bom-stock-location-row-${c.product_id}-${li}`}>
                                <td className="border border-[#D0D5DD] px-2 py-1.5" colSpan={bomStockStatus.commitments ? 8 : 5}></td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5">{loc.site || "—"}</td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5">{loc.warehouse || "—"}</td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5">{isDefaultStatus ? "—" : (loc.stock_status || "—")}</td>
                                <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(loc.qty)} {formatUnit(c.unit_of_measure)}</td>
                              </tr>
                            );
                          })}
                        </Fragment>
                      );
                    })}
                    {bomStockComponents.length === 0 && (
                      <tr><td colSpan={bomStockStatus?.commitments ? 12 : 9} className="text-center py-6 text-[#98A2B3] border border-[#D0D5DD]">
                        {bomStockSearch.trim() || bomStockShortageOnly ? "No components match your search/filter." : "No active components in this BOM."}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {bomStockStatus && !bomStockStatus.checked && (
            <p className="text-sm text-[#98A2B3]" data-testid="bom-stock-status-unavailable">{bomStockStatus.reason}</p>
          )}
        </DialogContent>
      </Dialog>

      <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-auto" data-testid="active-orders-card">
        <div className="px-3 py-2 border-b border-[#D0D5DD] bg-[#F9FAFB]">
          <h3 className="font-heading text-xs font-bold text-[#1D2939] uppercase tracking-wide">Active Orders ({activeJobs.length})</h3>
        </div>
        <table className="w-full text-[12px] border-collapse" data-testid="active-orders-table">
          <thead>
            <tr>
              {["Started", "Material", "Site", "Qty", "Status", "Actions"].map((h) => (
                <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {activeJobs.map((j, i) => {
              const isPaused = PAUSED_STATUSES.includes(j.status);
              return (
                <Fragment key={j.job_id}>
                  <tr className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`active-order-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{new Date(j.startedAt).toLocaleTimeString("en-IN")}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium">{j.material_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{j.site_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(j.quantity)} {formatUnit(j.unit_code)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`active-order-status-${i}`}>
                      {isPaused ? (
                        <>
                          <Badge variant="outline" className={`border ${
                            j.status === "partial_pending_planner" ? "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]"
                            : "bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]"
                          }`}>
                            {PHASE_LABELS[j.status] || j.status}
                          </Badge>
                          <PausedTimingNote job={j} index={i} />
                        </>
                      ) : j.status === "paused_sap_block" ? (
                        <>
                          <Badge variant="outline" className="border bg-[#FFFAEB] text-[#B54708] border-[#FEDF89]" data-testid={`active-order-sap-block-badge-${i}`}>
                            Paused - SAP Rejected
                          </Badge>
                          <PausedTimingNote job={j} index={i} />
                        </>
                      ) : j.status === "failed" ? (
                        <Badge variant="outline" className="border bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">
                          Failed
                        </Badge>
                      ) : (
                        <>
                          <OrderStepTracker status={j.status} elapsedSeconds={j.elapsedSeconds} progress={j.progress} />
                          {j.status === "waiting_for_order" && j.releaseTriggerCount > 0 && (
                            <div className="text-[11px] text-[#667085] mt-1" data-testid={`active-order-trigger-detail-${i}`}>
                              Waiting for SAP to convert Proposal {j.productionProposalId || "—"} into an Order - trigger attempt #{j.releaseTriggerCount}
                              {j.lastReleaseTriggerOk === false && j.lastReleaseTriggerError && (
                                j.lastReleaseTriggerError.toLowerCase().includes("already requested") ? (
                                  <span className="text-[#175CD3]"> (SAP already accepted an earlier attempt - confirming the resulting Order now)</span>
                                ) : (
                                  <span className="text-[#B54708]"> ({j.lastReleaseTriggerError})</span>
                                )
                              )}
                            </div>
                          )}
                          {j.status === "waiting_for_order" && j.proposalStatus && (
                            <div className="text-[11px] text-[#475467] mt-0.5" data-testid={`active-order-proposal-status-${i}`}>
                              SAP says about Proposal {j.productionProposalId || "—"}: <span className="font-semibold">{j.proposalStatus}</span>
                            </div>
                          )}
                          {j.productionOrderId && j.status === "releasing_order" && (
                            <OrderReleaseNote job={j} index={i} onUseOrderId={setReleaseOrderId} />
                          )}
                        </>
                      )}
                      {j.sfgShortage && (
                        <Badge variant="outline" className="border bg-[#FFFAEB] text-[#B54708] border-[#FEDF89] mt-1 block w-fit" data-testid={`active-order-sfg-shortage-flag-${i}`}>
                          SFG Shortage - Allowed with flag
                        </Badge>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {j.status === "partial_pending_planner" ? (
                        <div className="flex flex-wrap gap-1.5">
                          <Button size="sm" variant="outline" onClick={() => toggleJobExpand(j.job_id)} data-testid={`active-order-view-button-${i}`}>{j.expanded ? "Hide" : "View"}</Button>
                          <Button size="sm" disabled={j.deciding} onClick={() => decideStoreRequest(j.job_id, j.storeRequest?._id, "approve")} data-testid={`store-approval-approve-button-${i}`}>Confirm</Button>
                          <Button size="sm" variant="outline" disabled={j.deciding} onClick={() => decideStoreRequest(j.job_id, j.storeRequest?._id, "reject")} data-testid={`store-approval-reject-button-${i}`}>Delete</Button>
                        </div>
                      ) : j.status === "waiting_store_approval" ? (
                        j.storeRequest ? (
                          <Button size="sm" variant="outline" onClick={() => toggleJobExpand(j.job_id)} data-testid={`active-order-view-button-${i}`}>{j.expanded ? "Hide" : "View"}</Button>
                        ) : <span className="text-[11px] text-[#98A2B3]">Loading...</span>
                      ) : j.status === "paused_sap_block" ? (
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            size="sm"
                            disabled={resumingJobId === j.job_id}
                            onClick={() => resumeFailedJob(j)}
                            title="Re-sends the Release to SAP for the Proposal/Order that already exists - use it once the SAP master data is fixed"
                            data-testid={`active-order-retry-sap-block-button-${i}`}
                          >
                            {resumingJobId === j.job_id ? "Retrying..." : "Retry"}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => removeActiveJob(j.job_id)} data-testid={`active-order-dismiss-button-${i}`}>Dismiss</Button>
                        </div>
                      ) : j.status === "failed" ? (
                        <div className="flex flex-wrap gap-1.5">
                          {j.failure?.reason !== "sfg_shortage" && j.failure?.reason !== "duplicate_store_request" && (
                            <Button
                              size="sm"
                              disabled={resumingJobId === j.job_id}
                              onClick={() => resumeFailedJob(j)}
                              title={j.failure?.production_proposal_id
                                ? "Picks up from the Proposal that already exists in SAP"
                                : "Nothing was created in SAP - runs this same order again"}
                              data-testid={`active-order-resume-button-${i}`}
                            >
                              {resumingJobId === j.job_id
                                ? (j.failure?.production_proposal_id ? "Resuming..." : "Retrying...")
                                : (j.failure?.production_proposal_id ? "Resume" : "Retry")}
                            </Button>
                          )}
                          <Button size="sm" variant="outline" onClick={() => removeActiveJob(j.job_id)} data-testid={`active-order-dismiss-button-${i}`}>Dismiss</Button>
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1.5">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={j.stopping}
                            onClick={() => stopTrackingJob(j.job_id)}
                            title="Stops this app's own tracking only - if a SAP Proposal was already created, it stays in SAP un-converted, it is not deleted"
                            data-testid={`active-order-stop-button-${i}`}
                          >
                            {j.stopping ? "Stopping..." : "Stop"}
                          </Button>
                          {j.status === "waiting_for_order" && j.elapsedSeconds >= 120 && !(j.lastReleaseTriggerError || "").toLowerCase().includes("already requested") && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={j.retrying}
                              onClick={() => forceRetrigger(j.job_id)}
                              title="Fire the SAP Release trigger right now instead of waiting for the next automatic attempt"
                              data-testid={`active-order-retry-now-button-${i}`}
                            >
                              {j.retrying ? "Retrying..." : "Retry Now"}
                            </Button>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>

                  {j.sfgShortage && (
                    <tr data-testid={`active-order-sfg-shortage-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FFFAEB]">
                        <p className="text-[11px] text-[#B54708] mb-1.5">
                          SFG Shortage - Allowed with flag: {j.sfgShortage.short_components.length} sub-assembly component(s) short at {j.sfgShortage.site_id}, but the Proposal was created anyway.
                          These are produced in-house, not stocked by the Store.
                        </p>
                        <table className="w-full text-[11px] border-collapse bg-white">
                          <thead>
                            <tr>
                              {["Component", "Required by Production", `Available in ${j.sfgShortage.site_id}-SFG`, "Short By"].map((h) => (
                                <th key={h} className="border border-[#FEDF89] px-2 py-1 text-left font-bold text-[#B54708]">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {j.sfgShortage.short_components.map((c) => (
                              <tr key={c.product_id} data-testid={`active-order-sfg-shortage-row-${i}-${c.product_id}`}>
                                <td className="border border-[#FEDF89] px-2 py-1">{c.product_id}{c.description ? ` - ${c.description}` : ""}</td>
                                <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums">{formatInputQty(c.required_qty)} {formatUnit(c.unit_of_measure)}<RoundedUpFlag component={c} /></td>
                                <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums">{c.available_qty == null ? "unknown" : `${formatQty(c.available_qty)} ${formatUnit(c.unit_of_measure)}`}</td>
                                <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums font-bold text-[#B54708]">{formatQty(Math.max(0, c.required_qty - (c.available_qty || 0)))} {formatUnit(c.unit_of_measure)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  {j.commitmentWarning?.components?.length > 0 && (
                    <tr data-testid={`active-order-commitment-warning-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#F4F3FF]">
                        <p className="text-[11px] text-[#5925DC] mb-1.5">
                          <strong>Already promised elsewhere:</strong> {j.commitmentWarning.components.length} component(s) have enough physical stock at {j.commitmentWarning.site_id},
                          but it is already committed to other open production orders (demand from lots created in the last {j.commitmentWarning.lookback_days} days).
                          SAP refuses to release an order in this state when its Production Model disallows release on infeasible components.
                        </p>
                        <table className="w-full text-[11px] border-collapse bg-white">
                          <thead>
                            <tr>
                              {["Component", "Needed Here", `On Hand at ${j.commitmentWarning.site_id} (all godowns)`, "Committed", "Free", "Promised To"].map((h) => (
                                <th key={h} className="border border-[#D9D6FE] px-2 py-1 text-left font-bold text-[#5925DC]">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {j.commitmentWarning.components.map((c) => (
                              <tr key={c.product_id} data-testid={`active-order-commitment-row-${i}-${c.product_id}`}>
                                <td className="border border-[#D9D6FE] px-2 py-1">{c.product_id}{c.description ? ` - ${c.description}` : ""}</td>
                                <td className="border border-[#D9D6FE] px-2 py-1 text-right tabular-nums">{formatInputQty(c.required_qty)} {formatUnit(c.unit_of_measure)}</td>
                                <td className="border border-[#D9D6FE] px-2 py-1 text-right tabular-nums">{formatQty(c.available_qty)} {formatUnit(c.unit_of_measure)}</td>
                                <td className="border border-[#D9D6FE] px-2 py-1 text-right tabular-nums">
                                  {formatQty(c.committed_qty)} {formatUnit(c.unit_of_measure)}{c.claimed_qty > 0 ? ` (+${formatQty(c.claimed_qty)} in flight)` : ""}
                                </td>
                                <td className="border border-[#D9D6FE] px-2 py-1 text-right tabular-nums font-bold text-[#5925DC]">{formatQty(c.free_qty)} {formatUnit(c.unit_of_measure)}</td>
                                <td className="border border-[#D9D6FE] px-2 py-1">
                                  {(c.committed_to || []).map((o) => `${o.production_order_id} (${formatQty(o.qty)})`).join(", ") || "—"}
                                  {c.committed_lot_count > (c.committed_to || []).length ? ` +${c.committed_lot_count - (c.committed_to || []).length} more` : ""}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                  {j.status === "paused_sap_block" && (
                    <tr data-testid={`active-order-sap-block-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FFFAEB]">
                        <p className="text-[11px] text-[#B54708] mb-1">
                          <strong>SAP says:</strong>{" "}
                          <span className="font-mono" data-testid={`active-order-sap-block-error-${i}`}>
                            {j.sapError || j.failure?.sap_block?.sap_error || "Release did not go through"}
                          </span>
                        </p>
                        <p className="text-[11px] text-[#93370D]" data-testid={`active-order-sap-block-message-${i}`}>{j.error}</p>
                        {j.failure?.sap_block?.deleted_proposal_id ? (
                          <p className="text-[11px] text-[#93370D] mt-1" data-testid={`active-order-dead-proposal-${i}`}>
                            Proposal <strong>{j.failure.sap_block.deleted_proposal_id}</strong>{" "}
                            {j.failure.sap_block.proposal_deleted
                              ? "was deleted in SAP - it was created against a source of supply SAP has since replaced, so it could never become an Order."
                              : "could NOT be deleted in SAP automatically - close it there yourself, it can never become an Order."}{" "}
                            Fix the Production Model in SAP, then create this order again (Retry cannot help here).
                          </p>
                        ) : (
                          <p className="text-[11px] text-[#93370D] mt-1">
                            Proposal <strong>{j.failure?.production_proposal_id || j.productionProposalId}</strong>
                            {j.failure?.production_order_id ? <> and Order <strong>{j.failure.production_order_id}</strong></> : null} already exist in SAP and were not consumed -
                            the app stopped retrying so nothing piles up. Fix it in SAP and click <strong>Retry</strong>
                            {j.failure?.production_order_id ? <>, or release order <strong>{j.failure.production_order_id}</strong> yourself in SAP</> : null}.
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                  {j.status === "failed" && j.failure && (
                    <tr data-testid={`active-order-pipeline-error-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FEF3F2]">
                        <p className="text-[11px] text-[#B42318] mb-1" data-testid={`active-order-pipeline-error-reason-${i}`}>
                          <strong>Why this failed:</strong> {j.error || "SAP reported an unexpected error"}
                        </p>
                        {j.failure.reason === "duplicate_store_request" ? (
                          <p className="text-[11px] text-[#93370D]">
                            Nothing new was created in SAP - an identical open Store Request already covers this need.
                            Check the Store Approval screen instead of creating another order for the same shortage.
                          </p>
                        ) : j.failure.reason === "components_committed" ? (
                          <p className="text-[11px] text-[#93370D]" data-testid={`active-order-release-blocked-${i}`}>
                            Order <strong>{j.failure.production_order_id}</strong> exists in SAP but is still <strong>In Preparation</strong> - nothing was consumed.
                            Release it from SAP once the stock is genuinely free, or free it by confirming/closing the orders holding it (listed above).
                          </p>
                        ) : j.failure.production_proposal_id ? (
                          <p className="text-[11px] text-[#93370D]">
                            Proposal <strong>{j.failure.production_proposal_id}</strong>
                            {j.failure.production_order_id ? <> and Order <strong>{j.failure.production_order_id}</strong></> : null} already exist in SAP -
                            click <strong>Resume</strong> to let the app pick up from here automatically (no need to open the SAP UI).
                          </p>
                        ) : (
                          <p className="text-[11px] text-[#93370D]">
                            Nothing was created in SAP before this failed - click <strong>Retry</strong> to run this same order again.
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                  {j.expanded && j.storeRequest && (
                    <tr data-testid={`active-order-details-${i}`}>
                      <td colSpan={6} className="border border-[#D0D5DD] px-2 py-2 bg-[#FFFAEB]">
                        <p className="text-[11px] text-[#93370D] mb-1.5">
                          Proposal <strong>{j.storeRequest.production_proposal_id}</strong> was created in SAP - {j.storeRequest.components.length} component(s) short at {j.storeRequest.site_id}.
                        </p>
                        <table className="w-full text-[11px] border-collapse bg-white">
                          <thead>
                            <tr>
                              {["Component", "Required by Production", `In Stock at Site ${j.storeRequest.site_id} (By Warehouse)`, ...(j.status === "partial_pending_planner" ? ["Issued", "Shortfall"] : [])].map((h) => (
                                <th key={h} className="border border-[#FEDF89] px-2 py-1 text-left font-bold text-[#93370D]">{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {j.storeRequest.components.map((c) => (
                              <tr key={c.product_id}>
                                <td className="border border-[#FEDF89] px-2 py-1 align-top">{c.product_id}{c.description ? ` - ${c.description}` : ""}</td>
                                <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums align-top">{formatInputQty(c.required_qty)} {formatUnit(c.unit_of_measure)}<RoundedUpFlag component={c} /></td>
                                <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums align-top">
                                  {(c.locations && c.locations.length > 0) ? c.locations.map((loc, li) => {
                                    const restricted = isLocationRestricted(loc);
                                    return (
                                      <div key={li} className={restricted ? "text-[#B54708] font-bold" : ""}>
                                        {loc.warehouse || (loc.site ? loc.site.split("-").pop() : "Unknown Warehouse")}{loc.stock_status ? ` (${loc.stock_status})` : ""}{restricted ? " \u26A0 On Hold" : ""}: {formatQty(loc.qty)} {formatUnit(c.unit_of_measure)}
                                      </div>
                                    );
                                  }) : "no stock at this site"}
                                </td>
                                {j.status === "partial_pending_planner" && (
                                  <>
                                    <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums align-top">{formatQty(c.issued_qty)} {formatUnit(c.unit_of_measure)}</td>
                                    <td className="border border-[#FEDF89] px-2 py-1 text-right tabular-nums font-bold text-[#B42318] align-top">{formatQty(c.shortfall)} {formatUnit(c.unit_of_measure)}</td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {j.status === "waiting_store_approval" && (
                          <p className="text-[11px] text-[#93370D] mt-1.5">
                            Share this Request ID with the store team on the Store Approval screen (<code>/storeapproval</code>): <strong data-testid={`store-approval-request-id-${i}`}>{j.storeRequest._id}</strong>
                          </p>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {activeJobs.length === 0 && (
              <tr><td colSpan={6} className="text-center py-6 text-[#98A2B3] border border-[#D0D5DD]" data-testid="active-orders-empty-state">No orders in progress - anything you create above will show up here.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="bg-white border border-[#D0D5DD] rounded-sm overflow-auto">
        <div className="flex flex-wrap items-center gap-2 p-2 border-b border-[#D0D5DD] bg-[#F9FAFB]">
          <Input type="date" value={historyStartDate} onChange={(e) => setHistoryStartDate(e.target.value)} className="w-36 bg-white h-8 text-xs" data-testid="history-start-date-input" />
          <span className="text-[#98A2B3] text-xs">to</span>
          <Input type="date" value={historyEndDate} onChange={(e) => setHistoryEndDate(e.target.value)} className="w-36 bg-white h-8 text-xs" data-testid="history-end-date-input" />
          <Select value={historyResultFilter} onValueChange={setHistoryResultFilter}>
            <SelectTrigger className="w-36 bg-white h-8 text-xs" data-testid="history-result-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Results</SelectItem>
              <SelectItem value="released">Released</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
              <SelectItem value="created">Created</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
            </SelectContent>
          </Select>
          <Input placeholder="Filter by Model ID..." value={historyModelFilter} onChange={(e) => setHistoryModelFilter(e.target.value)} className="w-44 bg-white h-8 text-xs" data-testid="history-model-filter-input" />
          <Input placeholder="Filter by Product..." value={historyProductFilter} onChange={(e) => setHistoryProductFilter(e.target.value)} className="w-44 bg-white h-8 text-xs" data-testid="history-product-filter-input" />
          {(historyStartDate || historyEndDate || historyResultFilter !== "all" || historyModelFilter || historyProductFilter) && (
            <Button
              variant="outline" size="sm" className="h-8 text-xs"
              onClick={() => { setHistoryStartDate(""); setHistoryEndDate(""); setHistoryResultFilter("all"); setHistoryModelFilter(""); setHistoryProductFilter(""); }}
              data-testid="history-clear-filters-button"
            >
              Clear Filters
            </Button>
          )}
        </div>
        <table className="w-full text-[12px] border-collapse" data-testid="proposal-history-table">
          <thead>
            <tr>
              {["When", "By", "Action", "Product/Order", "Production Model", "Site", "Qty", "Result"].map((h) => (
                <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredHistory.map((h, i) => (
              <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`proposal-history-row-${i}`}>
                <td className="border border-[#D0D5DD] px-2 py-1">{new Date(h.at).toLocaleString("en-IN")}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">{h.actor}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">
                  {h.type !== "proposal_created" ? "Order Released" : h.production_order_id ? "Proposal → Order" : "Proposal Created"}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1">
                  {h.type !== "proposal_created"
                    ? h.production_order_id
                    : h.production_order_id
                      ? `${h.material_id} (Proposal ${h.production_proposal_id} \u2192 Order ${h.production_order_id})`
                      : `${h.material_id} (Proposal ${h.production_proposal_id})`}
                </td>
                <td className="border border-[#D0D5DD] px-2 py-1 text-[#475467]" data-testid={`proposal-history-model-cell-${i}`}>{h.production_model_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">{h.site_id || "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1 text-right tabular-nums">{h.quantity ?? "—"}</td>
                <td className="border border-[#D0D5DD] px-2 py-1">
                  {h.type !== "proposal_created"
                    ? h.success ? <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">Released</Badge> : <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Failed</Badge>
                    : h.production_order_id
                      ? h.released ? <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6]">Released</Badge> : <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA]">Release Failed</Badge>
                      : (
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className="bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]">Created</Badge>
                            {h.can_retry ? (
                              <Button
                                size="sm" variant="outline" className="h-6 px-2 text-[11px]"
                                disabled={retryingProposalId === h.production_proposal_id}
                                onClick={() => retryFromProposal(h)}
                                data-testid={`proposal-history-retry-button-${i}`}
                              >
                                {retryingProposalId === h.production_proposal_id ? "Retrying..." : "Retry"}
                              </Button>
                            ) : (
                              <span className="text-[11px] text-[#98A2B3]" data-testid={`proposal-history-in-progress-label-${i}`}>Still in progress</span>
                            )}
                          </div>
                          {h.sap_error && (
                            <span className="text-[11px] text-[#B54708] font-mono break-words max-w-[320px]" title={h.sap_error_message || ""} data-testid={`proposal-history-sap-error-${i}`}>
                              SAP: {h.sap_error}
                            </span>
                          )}
                        </div>
                      )}
                </td>
              </tr>
            ))}
            {!loadingHistory && filteredHistory.length === 0 && (
              <tr><td colSpan={8} className="text-center py-6 text-[#98A2B3] border border-[#D0D5DD]">
                {(historyStartDate || historyEndDate || historyResultFilter !== "all" || historyModelFilter || historyProductFilter) ? "No rows match the current filters." : "No Production Orders created yet."}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// -------------------- Main page --------------------
export default function ProductionConfirmationPage() {
  // Aug 2026, user's explicit ask (same change as Store Approval): the
  // manual "Your name" box is gone - this page already requires Entra ID
  // login, so the acting person's name comes straight from their
  // signed-in session instead.
  const { user } = useAuth();
  const actorName = user?.name || user?.email || "";
  const [statusFilter, setStatusFilter] = useState("open");
  const [creatorFilter, setCreatorFilter] = useState("all");
  const [sortLatestFirst, setSortLatestFirst] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  // Nov 4 2026: the SERVER holds the lot list now - these track its state so
  // the screen can offer "new data ready" instead of refreshing itself.
  const [snapshotAsOf, setSnapshotAsOf] = useState(null);
  const [newDataAsOf, setNewDataAsOf] = useState(null);
  const [serverBusy, setServerBusy] = useState(false);
  const [closingKey, setClosingKey] = useState(null);
  const [authError, setAuthError] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [manualLotId, setManualLotId] = useState("");
  const [confirmRow, setConfirmRow] = useState(null);
  const [qualityRow, setQualityRow] = useState(null);
  const [reasons, setReasons] = useState([]);
  const [showReasons, setShowReasons] = useState(false);
  const [stockByRow, setStockByRow] = useState({});
  const [lastConfirmationByLot, setLastConfirmationByLot] = useState({});
  const [outputProductFilter, setOutputProductFilter] = useState("");

  const loadReasons = useCallback(() => {
    axios.get(`${API}/production-confirmation/deviation-reasons`).then(({ data }) => setReasons(data.reasons)).catch(() => {});
  }, []);

  useEffect(() => {
    if (rows.length === 0) {
      setStockByRow({});
      return;
    }
    axios.post(`${API}/production-confirmation/component-availability-batch`, {
      rows: rows.map((r) => ({ main_output_product: r.main_output_product, quantity: r.open_quantity || 0, site_id: r.site_id, material_inputs: r.material_inputs || null })),
    }).then(({ data }) => {
      const map = {};
      rows.forEach((r, i) => { map[rowKey(r)] = data.results[i]; });
      setStockByRow(map);
    }).catch(() => {});
  }, [rows]);

  useEffect(() => {
    const lotIds = [...new Set(rows.map((r) => r.production_lot_id))];
    if (lotIds.length === 0) {
      setLastConfirmationByLot({});
      return;
    }
    axios.post(`${API}/production-confirmation/history/latest-batch`, { production_lot_ids: lotIds })
      .then(({ data }) => setLastConfirmationByLot(data))
      .catch(() => {});
  }, [rows]);

  // Nov 4 2026, user's ask: "DO NOT REFRESH FRONTEND SCREEN OF PRODUCTION
  // CONFIRMATION ... USE BACKEND REFRESH". The screen reads the snapshot the
  // SERVER keeps (instant) and never goes to SAP itself; the refresh runs on
  // the server and the operator is told when the new list is ready, so the
  // table they are working in only changes when they say so.
  const snapshotParams = useCallback(() => ({
    status: statusFilter, resolve_rp_descriptions: true,
  }), [statusFilter]);

  const startServerRefresh = useCallback(async () => {
    setServerBusy(true);
    try {
      await axios.post(`${API}/production-confirmation/open-lots/refresh`, null, { params: snapshotParams() });
    } catch (e) {
      setServerBusy(false);
      toast.error(e.response?.data?.detail || "Could not start the refresh on the server");
    }
  }, [snapshotParams]);

  const loadOpenLots = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    setAuthError(null);
    setLoadError(null);
    try {
      // Oct 3 2026, user's follow-up: the Reporting Point DESCRIPTION
      // ("BLANK+PUNCH+BENDING", "POWDER COATING") must show here too, not
      // just on the Admin Test page - `resolve_rp_descriptions` lets the
      // backend find the Production Model (and its Bill of Operations) for
      // lots this app never created itself. Cached per product+site.
      const { data } = await axios.get(`${API}/production-confirmation/open-lots`, {
        params: { ...snapshotParams(), source: "snapshot" },
      });
      setRows(data.rows);
      setSnapshotAsOf(data.as_of || null);
      setNewDataAsOf(null);
      if (data.refreshing) setServerBusy(true);
      if (!data.rows.length && !data.as_of) startServerRefresh();
    } catch (e) {
      if (e.response?.status === 403) setAuthError(e.response.data.detail);
      else setLoadError(e.response?.data?.detail || "Failed to load production lots from SAP");
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotParams]);

  // Polls the SERVER's snapshot state only - a few bytes, never the rows, so
  // nothing on screen moves while SAP is being read.
  useEffect(() => {
    if (!serverBusy) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/production-confirmation/open-lots/status`, { params: snapshotParams() });
        if (cancelled) return;
        if (!data.refreshing) {
          setServerBusy(false);
          if (data.error) toast.error(`SAP refresh failed: ${data.error}`);
          else if (data.as_of && data.as_of !== snapshotAsOf) {
            if (rows.length === 0) loadOpenLots({ silent: true });
            else setNewDataAsOf(data.as_of);
          }
        }
      } catch {
        if (!cancelled) setServerBusy(false);
      }
    };
    const timer = setInterval(tick, 4000);
    return () => { cancelled = true; clearInterval(timer); };
  }, [serverBusy, snapshotParams, snapshotAsOf, rows.length, loadOpenLots]);

  // Nov 4 2026: one button undoes both things that close a step - the task's
  // status AND the Reporting Point's Confirmation Finished indicator (SAP
  // accepts unchecking it, verified on task 83946).
  const [reopeningKey, setReopeningKey] = useState(null);
  const reopenStep = async (row) => {
    setReopeningKey(rowKey(row));
    try {
      await axios.post(`${API}/production-confirmation/restart-task`, {
        production_lot_id: row.production_lot_id,
        production_lot_uuid: row.production_lot_uuid,
        confirmation_group_uuid: row.confirmation_group_uuid,
        production_task_id: row.production_task_id,
        production_task_uuid: row.production_task_uuid,
        reporting_point_uuid: row.reporting_point_uuid,
        unit_code: row.unit_code,
        actor: actorName.trim() || "Unknown",
      });
      toast.success(`${row.reporting_point_description || row.reporting_point_id} reopened in SAP - you can post the rest now`);
      setRows((prev) => prev.map((r) => (rowKey(r) === rowKey(row)
        ? { ...r, task_finished: false, confirmation_finished: false } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not reopen this step in SAP");
    } finally {
      setReopeningKey(null);
    }
  };

  const closeStep = async (row) => {
    // Nov 4 2026, user's ask (Lot 82191: "RP_10 already match with planned
    // qty"): a step whose good + scrap have used up the plan has nothing left
    // to book - this closes the task in SAP, no quantity is sent.
    setClosingKey(rowKey(row));
    try {
      await axios.post(`${API}/production-confirmation/finish-task`, {
        production_lot_id: row.production_lot_id,
        production_lot_uuid: row.production_lot_uuid,
        confirmation_group_uuid: row.confirmation_group_uuid,
        production_task_id: row.production_task_id,
        production_task_uuid: row.production_task_uuid,
        reporting_point_id: row.reporting_point_id,
        reporting_point_description: row.reporting_point_description,
        production_model_id: row.production_model_id,
        site_id: row.site_id,
        main_output_product: row.main_output_product,
        confirmed_quantity: row.total_confirmed_quantity,
        unit_code: row.unit_code,
        actor: actorName.trim() || "Unknown",
      });
      toast.success(`${row.reporting_point_description || row.reporting_point_id} closed in SAP`);
      setRows((prev) => prev.map((r) => (rowKey(r) === rowKey(row) ? { ...r, task_finished: true } : r)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not close this step in SAP");
    } finally {
      setClosingKey(null);
    }
  };

  useEffect(() => { loadReasons(); }, [loadReasons]);
  useEffect(() => { loadOpenLots(); }, [loadOpenLots]);

  const lookupManual = async () => {
    if (!manualLotId.trim()) return;
    setLoading(true);
    setAuthError(null);
    setLoadError(null);
    try {
      const { data } = await axios.get(`${API}/production-confirmation/lot/${manualLotId.trim()}`, { params: { resolve_rp_descriptions: true } });
      setRows(data.rows);
      toast.success(`Loaded Production Lot ${manualLotId.trim()}`);
    } catch (e) {
      if (e.response?.status === 403) setAuthError(e.response.data.detail);
      else toast.error(e.response?.data?.detail || "Production lot not found");
    } finally {
      setLoading(false);
    }
  };

  const onConfirmed = (row) => {
    setConfirmRow(null);
    setRows((prev) => prev.filter((r) => rowKey(r) !== rowKey(row)));
  };

  // "Show mine" compares against the same actor string this app itself
  // writes on order creation (see createProposal's `actor: actorName.trim()`
  // below) - a case-insensitive match so a slightly different casing from
  // Entra ID doesn't silently hide a user's own orders.
  const visibleRows = useMemo(() => {
    let out = rows;
    if (creatorFilter === "mine") {
      const mine = actorName.trim().toLowerCase();
      out = out.filter((r) => (r.created_by || "").trim().toLowerCase() === mine);
    }
    if (outputProductFilter.trim()) {
      const q = outputProductFilter.trim().toLowerCase();
      out = out.filter((r) => (r.main_output_product || "").toLowerCase().includes(q));
    }
    if (sortLatestFirst) {
      out = [...out].sort((a, b) => {
        const at = a.order_created_at ? new Date(a.order_created_at).getTime() : -Infinity;
        const bt = b.order_created_at ? new Date(b.order_created_at).getTime() : -Infinity;
        return bt - at;
      });
    }
    return out;
  }, [rows, creatorFilter, sortLatestFirst, actorName, outputProductFilter]);

  // "How many pcs are sitting at OP10" (user's explicit ask, Aug 2026):
  // for a multi-step routing lot, the pieces that cleared THIS operation
  // but haven't been confirmed at the NEXT one yet are just sitting in
  // WIP between the two stages. Grouped from the full (unfiltered) `rows`
  // so the lookup always works regardless of search/sort/filter state,
  // keyed off each row's own key. Rows for a lot arrive from SAP already
  // in routing order (RP10 -> RP20 -> END), so "next" is simply the very
  // next row for the same lot - the last operation of a lot has no
  // "next", nothing is waiting beyond it (its output becomes real FG/SFG
  // stock, not in-process WIP).
  const stageWaitingByRowKey = useMemo(() => {
    const map = {};
    const byLot = {};
    rows.forEach((r) => { (byLot[r.production_lot_id] = byLot[r.production_lot_id] || []).push(r); });
    Object.values(byLot).forEach((lotRows) => {
      for (let i = 0; i < lotRows.length - 1; i++) {
        const cur = lotRows[i];
        const next = lotRows[i + 1];
        map[rowKey(cur)] = Math.max(0, (cur.total_confirmed_quantity || 0) - (next.total_confirmed_quantity || 0));
      }
    });
    return map;
  }, [rows]);

  return (
    <div className="print:hidden h-screen flex flex-col overflow-hidden bg-[#F2F4F7] text-[#1D2939]">

      <header className="min-h-16 bg-[#0E7C86] shadow-[0_1px_3px_0_rgba(16,24,40,0.15)] flex items-center justify-between px-3 sm:px-5 shrink-0 z-10 gap-2 sm:gap-4">
        <div className="flex items-center gap-3 shrink-0" data-testid="app-title">
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Shield size={18} weight="fill" className="text-white" />
          </div>
          <div className="flex flex-col leading-tight">
            <span className="font-heading text-[16px] font-bold text-white tracking-tight">Production Control</span>
            <span className="font-sans text-[12px] text-white/70 hidden sm:inline">Production Confirmation</span>
          </div>
        </div>
        <div className="w-px h-7 bg-white/25 shrink-0" />
        <div className="flex items-center gap-3 flex-1 justify-start min-w-0">
          <NavTabs />
        </div>
        <SapConnectionStatus />
        <ErpConnectionStatus />
      </header>

      <main className="flex-1 overflow-auto p-4 space-y-4">
        <Tabs defaultValue="create" className="space-y-4">
          <TabsList data-testid="page-tabs">
            <TabsTrigger value="create" data-testid="tab-create-order">Create Production Order</TabsTrigger>
            <TabsTrigger value="confirm" data-testid="tab-confirm-production">Production Confirmation</TabsTrigger>
            <TabsTrigger value="my-requests" data-testid="tab-my-requests">My Stock Requests</TabsTrigger>
            <TabsTrigger value="return-to-store" data-testid="tab-return-to-store">Return to Store</TabsTrigger>
            <TabsTrigger value="npd-issues" data-testid="tab-npd-issues">Issue From NPD</TabsTrigger>
          </TabsList>

          <TabsContent value="create">
            <CreateOrderTab actorName={actorName} />
          </TabsContent>

          <TabsContent value="my-requests">
            <MyStockRequestsTab actorName={actorName} />
          </TabsContent>

          <TabsContent value="return-to-store">
            <ReturnToStoreTab actorName={actorName} />
          </TabsContent>

          {/* Oct 27 2026, user: "from the Production page next to return to
              Store. When user accept it Goods movement trigger NPD warehouse to
              SFG warehouse" - NPD only raises; the movement is fired here. */}
          <TabsContent value="npd-issues">
            <NpdIssueForProductionTab sites={[]} canRaise={false} allowDecide />
          </TabsContent>

          <TabsContent value="confirm" className="space-y-4">
        {authError && (
          <Alert className="bg-[#FFFAEB] border-[#FEDF89]" data-testid="auth-error-banner">
            <WarningCircle size={16} className="text-[#B54708]" />
            <AlertTitle className="text-[#B54708]">SAP Authorization Required</AlertTitle>
            <AlertDescription className="text-[#B54708] text-sm">
              {authError} Ask your SAP admin to go to <strong>Application and User Management → Communication Arrangements</strong>,
              activate the <strong>"Query Production Lots"</strong> and <strong>"Manage Production Lots"</strong> Communication Scenarios,
              and authorize the technical user <strong>_EMERGENTBOM</strong> for them.
            </AlertDescription>
          </Alert>
        )}
        {loadError && !authError && (
          <Alert className="bg-[#FEF3F2] border-[#FECDCA]" data-testid="load-error-banner">
            <WarningCircle size={16} className="text-[#B42318]" />
            <AlertDescription className="text-[#B42318] text-sm">{loadError}</AlertDescription>
          </Alert>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56 bg-white" data-testid="status-filter-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open (Released/Started)</SelectItem>
              <SelectItem value="4" data-testid="status-filter-finished">Finished</SelectItem>
              <SelectItem value="all">All Statuses</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={startServerRefresh} disabled={serverBusy} data-testid="refresh-lots-button">
            <ArrowClockwise size={14} className={`mr-1.5 ${serverBusy ? "animate-spin" : ""}`} /> {serverBusy ? "Server reading SAP..." : "Refresh"}
          </Button>
          <div className="w-px h-6 bg-[#D0D5DD] mx-1" />
          {user?.role !== "user" && (
            <Select value={creatorFilter} onValueChange={setCreatorFilter}>
              <SelectTrigger className="w-40 bg-white" data-testid="creator-filter-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all" data-testid="creator-filter-all">Show all</SelectItem>
                <SelectItem value="mine" data-testid="creator-filter-mine">Show mine</SelectItem>
              </SelectContent>
            </Select>
          )}
          <Input
            placeholder="Filter by Output Product..."
            value={outputProductFilter}
            onChange={(e) => setOutputProductFilter(e.target.value)}
            className="w-52 bg-white"
            data-testid="output-product-filter-input"
          />
          <Button
            variant="outline"
            onClick={() => setSortLatestFirst((s) => !s)}
            className={sortLatestFirst ? "bg-[#EFF8FF] text-[#175CD3] border-[#B2DDFF]" : ""}
            data-testid="sort-latest-toggle-button"
          >
            <ClockCounterClockwise size={14} className="mr-1.5" /> Sort by latest {sortLatestFirst ? "✓" : ""}
          </Button>
          <div className="w-px h-6 bg-[#D0D5DD] mx-1" />
          <Input
            placeholder="Enter Production Lot ID..."
            value={manualLotId}
            onChange={(e) => setManualLotId(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && lookupManual()}
            className="w-56 bg-white"
            data-testid="manual-lot-id-input"
          />
          <Button variant="outline" onClick={lookupManual} data-testid="manual-lookup-button">
            <MagnifyingGlass size={14} className="mr-1.5" /> Look Up
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={() => setShowReasons(true)} data-testid="open-manage-reasons-button">
            <Gear size={14} className="mr-1.5" /> Manage Reasons
          </Button>
        </div>

        {/* Nov 4 2026, user's ask ("DO NOT REFRESH FRONTEND SCREEN ... USE
            BACKEND REFRESH"): the 15-25s SAP read now happens on the SERVER.
            The screen shows the list the server holds and never rebuilds
            itself - when a newer list lands the operator is offered a button
            and decides when the table moves. */}
        {(newDataAsOf || serverBusy || snapshotAsOf) && (
          <div className="flex items-center justify-between gap-3 bg-white border border-[#D0D5DD] rounded-sm px-3 py-2 text-xs mb-3" data-testid="snapshot-strip">
            <span className="text-[#667085]">
              {snapshotAsOf ? <>Showing the list the server read at <strong className="text-[#1D2939]">{new Date(snapshotAsOf).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</strong></> : "No server list yet"}
              {serverBusy && <span className="text-[#475467]"> · the server is reading SAP now - nothing on this screen will move</span>}
            </span>
            {newDataAsOf && (
              <Button size="sm" onClick={() => loadOpenLots({ silent: true })} className="h-7 bg-[#027A48] hover:bg-[#026A3E]" data-testid="load-new-data-button">
                <ArrowClockwise size={12} weight="bold" className="mr-1.5" /> New data ready - load it
              </Button>
            )}
          </div>
        )}

        {loading && rows.length === 0 ? (
          <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-10 w-full rounded-sm" />)}</div>
        ) : (
          <div className="border border-[#D0D5DD] rounded-sm overflow-auto bg-white">
            <table className={`w-full text-[13px] border-collapse transition-opacity ${loading ? "opacity-60" : "opacity-100"}`} data-testid="production-lots-table">
              <thead>
                <tr>
                  {["Lot ID", "Output Product", "Site", "End Date", "Status", "Reporting Point", "Task ID", "Planned", "Confirmed So Far", "Open", "Waiting Next Stage", "UOM", "Finished", "Created By", "Production Model", "Stock", "Last Confirmation", ""].map((h) => (
                    <th key={h} className="bg-[#EAECF0] border border-[#D0D5DD] p-1.5 text-left text-xs font-bold text-[#344054] font-heading uppercase tracking-wide">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => {
                  const stock = stockByRow[rowKey(r)];
                  const lastConf = lastConfirmationByLot[`${r.production_lot_id}::${r.reporting_point_id}`];
                  return (
                  <tr key={rowKey(r)} className={r.locked ? "bg-[#F2F4F7] opacity-70" : i % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"} data-testid={`lot-row-${i}`}>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 font-medium text-[#101828]">{r.production_lot_id}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.main_output_product || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{r.site_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] whitespace-nowrap" data-testid={`end-date-cell-${i}`}>{formatEndDate(r.production_end_date)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      <Badge variant="outline" className={`${STATUS_TONE[r.life_cycle_status_label] || "bg-slate-100 text-slate-600 border-slate-200"} border`}>{r.life_cycle_status_label}</Badge>
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]" data-testid={`reporting-point-cell-${i}`}>
                      {/* Oct 3 2026, user's ask: the DESCRIPTION replaces the
                          code (no "(RP_10)" suffix); the raw code only shows
                          when SAP gives us no name for it. */}
                      {r.reporting_point_description || r.operation_description || r.reporting_point_id || "—"}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467] font-mono" data-testid={`task-id-cell-${i}`}>{r.production_task_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(r.planned_quantity)}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums">{formatQty(r.total_confirmed_quantity)}</td>
                    {/* Nov 4 2026 (Lot 82191): SAP's own Open counts only GOOD
                        output, so a step that physically finished its plan (3
                        good + 2 scrap of 5) still read "2 open" and looked
                        pending. Shows what is really left instead, with SAP's
                        own number on hover for anyone reconciling. */}
                    <td
                      className={`border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums font-bold ${(r.remaining_quantity ?? r.open_quantity) > 0 ? "text-[#B54708]" : "text-[#027A48]"}`}
                      title={r.remaining_quantity != null && r.remaining_quantity !== r.open_quantity ? `SAP reports ${formatQty(r.open_quantity)} open - that counts good output only. ${formatQty(r.total_confirmed_scrap)} scrapped units used up planned quantity too.` : undefined}
                      data-testid={`remaining-qty-cell-${i}`}
                    >
                      {formatQty(r.remaining_quantity ?? r.open_quantity)}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-right tabular-nums" data-testid={`stage-waiting-cell-${i}`}>
                      {stageWaitingByRowKey[rowKey(r)] > 0 ? (
                        <span className="text-[#175CD3] font-semibold" title={`Confirmed at ${r.operation_description || r.reporting_point_id} but not yet confirmed at the next operation`}>
                          {formatQty(stageWaitingByRowKey[rowKey(r)])}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]">{formatUnit(r.unit_code) || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">{r.task_finished ? "Yes" : "No"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]" data-testid={`created-by-cell-${i}`}>{r.created_by || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5 text-[#475467]" data-testid={`production-model-cell-${i}`}>{r.production_model_id || "—"}</td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {!stock ? (
                        <span className="text-[11px] text-[#98A2B3]" data-testid={`stock-badge-loading-${i}`}>…</span>
                      ) : !stock.checked ? (
                        <span className="text-[11px] text-[#98A2B3]" data-testid={`stock-badge-unknown-${i}`}>No BOM cached</span>
                      ) : stock.sufficient_all ? (
                        <Badge variant="outline" className="bg-[#ECFDF3] text-[#027A48] border-[#ABEFC6] border" data-testid={`stock-badge-ok-${i}`}>OK</Badge>
                      ) : (
                        <Popover>
                          <PopoverTrigger asChild>
                            <Badge variant="outline" className="bg-[#FEF3F2] text-[#B42318] border-[#FECDCA] border cursor-pointer" data-testid={`stock-badge-short-${i}`}>
                              Short ({stock.short_components.length})
                            </Badge>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto max-w-sm bg-white border border-[#D0D5DD] text-[#1D2939] shadow-lg p-3 text-xs space-y-1" data-testid={`stock-tooltip-${i}`}>
                            <div className="font-bold font-heading uppercase tracking-wide text-[10px] text-[#667085] mb-1">Short Components</div>
                            {stock.short_components.map((c) => (
                              <div key={c.product_id}>
                                {c.product_id}: need {formatInputQty(c.required_qty)} {formatUnit(c.unit_of_measure)}, have {c.available_qty === null ? "no data" : `${formatQty(c.available_qty)} ${formatUnit(c.unit_of_measure)}`}
                                <RoundedUpFlag component={c} />
                              </div>
                            ))}
                          </PopoverContent>
                        </Popover>
                      )}
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5" data-testid={`last-confirmation-cell-${i}`}>
                      <LastConfirmationBadges
                        data={lastConf}
                        productionLotId={r.production_lot_id}
                        lastConfKey={`${r.production_lot_id}::${r.reporting_point_id}`}
                        taskFinished={r.task_finished}
                        openQuantity={r.open_quantity}
                        siteId={r.site_id}
                        mainOutputProduct={r.main_output_product}
                        unitCode={r.unit_code}
                        actorName={actorName}
                        onRetried={(lotId, patch) => setLastConfirmationByLot((prev) => (
                          prev[lotId] ? { ...prev, [lotId]: { ...prev[lotId], ...patch } } : prev
                        ))}
                      />
                    </td>
                    <td className="border border-[#D0D5DD] px-2 py-1.5">
                      {r.locked ? (
                        <span className="inline-flex items-center gap-1 text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-1.5 py-0.5" data-testid={`lock-badge-${i}`} title={r.lock_reason}>
                          <LockSimple size={11} weight="bold" /> {r.lock_reason}
                        </span>
                      ) : (
                      <div className="flex items-center gap-1.5">
                        <Button
                          size="sm"
                          disabled={loading || r.task_finished || (r.max_confirmable != null && r.max_confirmable <= 0)}
                          onClick={() => setConfirmRow(r)}
                          title={
                            r.task_finished ? "Task genuinely Finished in SAP - re-confirmation disabled to avoid a duplicate posting"
                            : r.step_complete ? "Good + scrap have used up the planned quantity - nothing further can be booked here"
                            : r.awaiting_quality ? `${r.quality_held_quantity} is with quality (${r.quality?.note_id || "open note"}) - nothing can be booked until it is released`
                            : (r.max_confirmable != null && r.max_confirmable <= 0) ? `Nothing has cleared ${r.previous_step_label || "the previous step"} yet - confirm that step first`
                            : undefined
                          }
                          data-testid={`confirm-button-${i}`}
                        >
                          Confirm
                        </Button>
                        {/* Nov 4 2026 (Lot 82191: 3 good + 2 scrap of 5 planned) */}
                        {/* Nov 4 2026 (Lot 82274): a step closed with quantity
                            still open is NOT a dead end - SAP accepts both the
                            task restart and unchecking Confirmation Finished. */}
                        {(r.task_finished || r.confirmation_finished) && (r.remaining_quantity ?? 0) > 0 && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs bg-white"
                            disabled={reopeningKey === rowKey(r)}
                            onClick={() => reopenStep(r)}
                            title={`This step closed with ${formatQty(r.remaining_quantity)} ${formatUnit(r.unit_code)} still open - reopen it in SAP to post the rest`}
                            data-testid={`reopen-step-button-${i}`}
                          >
                            {reopeningKey === rowKey(r)
                              ? <CircleNotch size={13} className="animate-spin" />
                              : <><ArrowCounterClockwise size={13} weight="bold" className="mr-1" /> Reopen Step</>}
                          </Button>
                        )}
                        {r.step_complete && !r.task_finished && !r.awaiting_quality && (
                          <>
                            <span className="inline-flex items-center gap-1 text-[11px] text-[#027A48] bg-[#ECFDF3] border border-[#A6F4C5] rounded-sm px-1.5 py-0.5" data-testid={`step-complete-badge-${i}`}>
                              <CheckCircle size={11} weight="fill" /> Complete
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={closingKey === rowKey(r)}
                              onClick={() => closeStep(r)}
                              title="Good + scrap have used up the planned quantity - close this step in SAP (no quantity is sent)"
                              data-testid={`close-step-button-${i}`}
                            >
                              {closingKey === rowKey(r) ? <CircleNotch size={14} className="animate-spin" /> : "Close Step"}
                            </Button>
                          </>
                        )}
                        {/* Nov 4 2026: Confirm can be disabled while quality
                            holds pieces, so the decision has to be readable
                            from the row itself, not only inside the dialog. */}
                        {r.quality?.note_id && (r.awaiting_quality || r.quality.cleared_quantity > 0) && (
                          <button
                            type="button"
                            onClick={() => setQualityRow(r)}
                            className="inline-flex items-center gap-1 text-[11px] text-[#B54708] bg-[#FFFAEB] border border-[#FEDF89] rounded-sm px-1.5 py-0.5 hover:bg-[#FEF0C7] transition-colors"
                            title="See what quality decided on this step"
                            data-testid={`awaiting-quality-badge-${i}`}
                          >
                            <WarningCircle size={11} weight="bold" />
                            {r.awaiting_quality
                              ? `With quality (${formatQty(r.quality.to_decide_quantity ?? r.quality_held_quantity)})`
                              : `Quality cleared ${formatQty(r.quality.cleared_quantity)}`}
                          </button>
                        )}
                      </div>
                      )}
                    </td>
                  </tr>
                  );
                })}
                {rows.length === 0 && authError && (
                  <tr><td colSpan={15} className="text-center py-8 text-[#B54708] bg-[#FFFAEB] border border-[#D0D5DD]" data-testid="blocked-state">Blocked by SAP authorization - see banner above.</td></tr>
                )}
                {rows.length === 0 && !authError && !loadError && (
                  <tr><td colSpan={15} className="text-center py-8 text-[#98A2B3] border border-[#D0D5DD]" data-testid="empty-state">No open production lots found.</td></tr>
                )}
                {rows.length > 0 && visibleRows.length === 0 && (
                  <tr><td colSpan={15} className="text-center py-8 text-[#98A2B3] border border-[#D0D5DD]" data-testid="filtered-empty-state">No rows match "Show mine" - no open lots were created by you.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
          </TabsContent>
        </Tabs>
      </main>

      <ConfirmDialog
        row={confirmRow}
        siblingRows={confirmRow ? rows.filter((r) => r.production_lot_id === confirmRow.production_lot_id) : []}
        actorName={actorName}
        onClose={() => setConfirmRow(null)}
        onConfirmed={onConfirmed}
        reasons={reasons}
        onReasonsRefresh={loadReasons}
      />
      <ManageReasonsDialog open={showReasons} onClose={() => setShowReasons(false)} reasons={reasons} onChanged={setReasons} />
      <QualityDecisionDialog row={qualityRow} unit={formatUnit(qualityRow?.unit_code)} onClose={() => setQualityRow(null)} />
    </div>
  );
}
